import {
  ArrowLeft,
  BarChart3,
  Building2,
  CalendarDays,
  ClipboardList,
  Clock,
  ExternalLink,
  LockKeyhole,
  LogOut,
  Medal,
  Plus,
  MapPin,
  Pencil,
  RefreshCcw,
  Save,
  Search,
  Shuffle,
  Trash2,
  Trophy,
  X,
  Users,
  Utensils
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  adminLogin,
  adminLogout,
  createBadmintonMatch,
  deleteBadmintonMatch,
  deleteBadmintonTeam,
  getBadmintonMatches,
  getBadmintonTeams,
  getBuSummary,
  getEvent,
  getAdminSession,
  getFootballRoster,
  getParticipants,
  getSports,
  getStats,
  importSheet,
  importBadmintonSheet,
  updateEvent,
  updateBadmintonMatch,
  updateBadmintonTeam
} from "./api";
import type {
  BadmintonGroupName,
  BadmintonMatch,
  BadmintonMatchDraft,
  BadmintonRoundType,
  BadmintonSetScore,
  BadmintonTeam,
  BadmintonTeamDraft,
  BadmintonTeamsResponse,
  BuSummary,
  EventSettings,
  FootballTeam,
  Participant,
  Sport,
  Stats,
  WinningScore
} from "./types";

const fallbackEvent: EventSettings = {
  id: 1,
  name: "SCS GROUP REUNION DAY 2026",
  event_date: "2026-06-13",
  start_time: "07:00",
  end_time: "12:00",
  location_name: "Sân Pickleball Cộng Hòa Garden",
  location_address: "20 Cộng Hòa, Phường Tân Sơn Nhất, Hồ Chí Minh",
  cta_label: "Đăng ký tham gia",
  cta_url:
    "https://docs.google.com/spreadsheets/d/1iBG-7y2jsYtFaG1P53Q9YId74vjZYUHM/edit?usp=sharing&ouid=103063079684729988386&rtpof=true&sd=true",
  sync_sheet_url:
    "https://docs.google.com/spreadsheets/d/1iBG-7y2jsYtFaG1P53Q9YId74vjZYUHM/edit?usp=sharing&ouid=103063079684729988386&rtpof=true&sd=true"
};

const fallbackSports: Sport[] = [
  { id: 1, slug: "badminton-women-doubles", name: "Cầu lông đôi nữ", category: "Cầu lông", team_size: 2, sort_order: 10 },
  { id: 2, slug: "badminton-men-doubles", name: "Cầu lông đôi nam", category: "Cầu lông", team_size: 2, sort_order: 20 },
  { id: 3, slug: "pickleball-doubles", name: "Pickleball đôi", category: "Pickleball", team_size: 2, sort_order: 30 },
  { id: 4, slug: "foosball-doubles", name: "Bi lắc đôi", category: "Bi lắc", team_size: 2, sort_order: 40 },
  { id: 5, slug: "football", name: "Bóng đá", category: "Bóng đá", team_size: 1, sort_order: 50 }
];

const fallbackFootballTeams: FootballTeam[] = [
  {
    name: "Đội SCS",
    players: [
      { order: 1, name: "Nộc LOKI" },
      { order: 2, name: "Stephen Luân" },
      { order: 3, name: "Brian Nhã" },
      { order: 4, name: "Terry Tiến" },
      { order: 5, name: "Levi Trung" },
      { order: 6, name: "Ralph Tín" },
      { order: 7, name: "John Danh" },
      { order: 8, name: "Top" },
      { order: 9, name: "Hugo Thông" },
      { order: 10, name: "Canh Pham" },
      { order: 11, name: "Eric Thuận" },
      { order: 12, name: "Victor Vân" },
      { order: 13, name: "Henry Hiệp" },
      { order: 14, name: "A Khoa" },
      { order: 15, name: "Peter" }
    ]
  },
  {
    name: "Đội SCT",
    players: [
      { order: 1, name: "Phạm Duy" },
      { order: 2, name: "Lương Duy Tùng" },
      { order: 3, name: "Võ Anh Tùng" },
      { order: 4, name: "Lâm Phát Toàn" },
      { order: 5, name: "Võ Hoàng Anh Kiệt" },
      { order: 6, name: "Nguyễn Hoàng Long" },
      { order: 7, name: "Nguyễn Trường Thành" },
      { order: 8, name: "Nguyễn Đình Duy" },
      { order: 9, name: "Lê Quang Nhân" }
    ]
  }
];

const fallbackFootballPlayerCount = fallbackFootballTeams.reduce((total, team) => total + team.players.length, 0);
const footballFinalScore = {
  championTeam: "SCT",
  championScore: 4,
  runnerUpTeam: "SCS",
  runnerUpScore: 2
};
const footballPodiumResults = [
  { rank: 1, team: "SCT" },
  { rank: 2, team: "SCS" }
];

type LandingLanguage = "vi" | "en";

const languageOptions: Array<{ value: LandingLanguage; label: string; flag: string; name: string }> = [
  { value: "vi", label: "VI", flag: "🇻🇳", name: "Tiếng Việt" },
  { value: "en", label: "EN", flag: "🇺🇸", name: "English" }
];

const landingCopy = {
  vi: {
    members: "Thành viên",
    lunch: "Ăn trưa",
    sports: "Môn thi đấu",
    buOverview: "Tổng quan BU",
    membersByBu: "Thành viên theo BU",
    buName: "Tên BU",
    count: "Số lượng",
    sportList: "Danh sách môn",
    sportHeading: "Thi đấu đồng đội, ghi danh rõ ràng",
    info: "Thông tin",
    viewMap: "Xem bản đồ",
    recognition: "Vinh danh",
    footballResults: "Kết quả bóng đá",
    championRunnerUp: "Vô địch & Á Quân",
    finalScore: "Tỷ số chung cuộc",
    champion: "Vô địch",
    runnerUp: "Á Quân",
    football: "Bóng đá",
    badmintonResults: "Kết quả cầu lông",
    topBadminton: "Top 3 đôi xuất sắc",
    rank: "Hạng",
    bu: "BU",
    closeFootballResults: "Đóng kết quả bóng đá",
    closeBadmintonResults: "Đóng kết quả cầu lông",
    teamRoster: "Danh sách đội",
    closeModal: "Đóng modal",
    viewSchedule: "Xem lịch trận",
    loadingTeams: "Đang tải danh sách đội...",
    group: "Bảng",
    ungrouped: "Chưa xếp bảng",
    teamUnit: "đội",
    playerUnit: "cầu thủ",
    noTeams: "Chưa có đội.",
    noMembers: "Chưa có thành viên",
    footballRosterTitle: "Danh sách 2 đội bóng đá"
  },
  en: {
    members: "Members",
    lunch: "Lunch",
    sports: "Sports",
    buOverview: "BU Overview",
    membersByBu: "Members by BU",
    buName: "BU Name",
    count: "Count",
    sportList: "Sports List",
    sportHeading: "Team events with clear registration",
    info: "Info",
    viewMap: "View Map",
    recognition: "Recognition",
    footballResults: "Football Results",
    championRunnerUp: "Champion & Runner-up",
    finalScore: "Final score",
    champion: "Champion",
    runnerUp: "Runner-up",
    football: "Football",
    badmintonResults: "Badminton Results",
    topBadminton: "Top 3 doubles teams",
    rank: "Rank",
    bu: "BU",
    closeFootballResults: "Close football results",
    closeBadmintonResults: "Close badminton results",
    teamRoster: "Team Roster",
    closeModal: "Close modal",
    viewSchedule: "View Schedule",
    loadingTeams: "Loading team roster...",
    group: "Group",
    ungrouped: "Ungrouped",
    teamUnit: "teams",
    playerUnit: "players",
    noTeams: "No teams yet.",
    noMembers: "No members yet",
    footballRosterTitle: "Two football teams"
  }
};

const landingSportLabels: Record<string, Record<LandingLanguage, { name: string; category: string }>> = {
  "badminton-women-doubles": {
    vi: { name: "Cầu lông đôi nữ", category: "Cầu lông" },
    en: { name: "Women's Doubles Badminton", category: "Badminton" }
  },
  "badminton-men-doubles": {
    vi: { name: "Cầu lông đôi nam", category: "Cầu lông" },
    en: { name: "Men's Doubles Badminton", category: "Badminton" }
  },
  "pickleball-doubles": {
    vi: { name: "Pickleball đôi", category: "Pickleball" },
    en: { name: "Doubles Pickleball", category: "Pickleball" }
  },
  "foosball-doubles": {
    vi: { name: "Bi lắc đôi", category: "Bi lắc" },
    en: { name: "Doubles Foosball", category: "Foosball" }
  },
  football: {
    vi: { name: "Bóng đá", category: "Bóng đá" },
    en: { name: "Football", category: "Football" }
  }
};

const showLandingBadmintonSchedule = false;
const showLandingBadmintonMatchResults = false;

const badmintonPodiumResults = [
  {
    title: { vi: "Cầu lông Nam", en: "Men's Badminton" },
    label: { vi: "Đôi nam", en: "Men's Doubles" },
    winners: [
      { rank: 1, names: ["Duy Quang", "Quốc Anh"], bu: "KGS" },
      { rank: 2, names: ["Trường Sinh", "Tuấn Anh"], bu: "SCT" },
      { rank: 3, names: ["Duy Tùng", "Anh Tùng"], bu: "SCT" }
    ]
  },
  {
    title: { vi: "Cầu lông Nữ", en: "Women's Badminton" },
    label: { vi: "Đôi nữ", en: "Women's Doubles" },
    winners: [
      { rank: 1, names: ["Ánh Nhật", "Lan Nhi"], bu: "KGS" },
      { rank: 2, names: ["Kim Thoa", "Hồng Nhạn"], bu: "KGS / XLT" },
      { rank: 3, names: ["Trường Trang", "Mỹ Quyên"], bu: "PHIM CHÍNH" }
    ]
  }
];

const logoSrc = "/scs-logo.png";
const googleMapsUrl =
  "https://www.google.com/maps/dir//Pickleball+C%E1%BB%99ng+H%C3%B2a+Garden,+20+C%E1%BB%99ng+H%C3%B2a,+T%C3%A2n+S%C6%A1n+Nh%E1%BA%A5t,+H%E1%BB%93+Ch%C3%AD+Minh+70000,+Vi%E1%BB%87t+Nam/@10.8007089,106.6880843,3384m/data=!3m1!1e3!4m8!4m7!1m0!1m5!1m1!1s0x317529006eb062ab:0x1d3b501b4a544eb4!2m2!1d106.6530385!2d10.8058931?entry=ttu&g_ep=EgoyMDI2MDUxMi4wIKXMDSoASAFQAw%3D%3D";

function App() {
  const badmintonMatch = window.location.pathname.match(/^\/admin\/badminton\/([^/]+)$/);
  if (badmintonMatch) {
    return (
      <AdminGate>
        <BadmintonAdminPage sportSlug={badmintonMatch[1]} />
      </AdminGate>
    );
  }

  return window.location.pathname.startsWith("/admin") ? (
    <AdminGate>
      <AdminPage />
    </AdminGate>
  ) : (
    <LandingPage />
  );
}

function AdminGate({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    getAdminSession()
      .then((session) => setAuthenticated(session.authenticated))
      .catch(() => setAuthenticated(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await adminLogout().catch(() => null);
    setAuthenticated(false);
  };

  if (checking) {
    return (
      <main className="admin-auth-shell">
        <section className="admin-auth-card">
          <p className="admin-kicker">Admin</p>
          <h1>Đang kiểm tra đăng nhập</h1>
        </section>
      </main>
    );
  }

  if (!authenticated) {
    return <AdminLoginPage onAuthenticated={() => setAuthenticated(true)} />;
  }

  return (
    <>
      <div className="admin-session-bar">
        <span>Đã đăng nhập: teresa-kathy</span>
        <button className="ghost-button" type="button" onClick={logout}>
          <LogOut size={16} />
          Đăng xuất
        </button>
      </div>
      {children}
    </>
  );
}

function AdminLoginPage({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    try {
      await adminLogin(username, password);
      onAuthenticated();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không đăng nhập được.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="admin-auth-shell">
      <form className="admin-auth-card" onSubmit={submit}>
        <div className="admin-auth-icon">
          <LockKeyhole size={26} />
        </div>
        <p className="admin-kicker">Admin</p>
        <h1>Đăng nhập quản trị</h1>
        <label className="field">
          Username
          <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
        </label>
        <label className="field">
          Password
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            autoComplete="current-password"
          />
        </label>
        {message ? <p className="auth-error">{message}</p> : null}
        <button className="primary-action" type="submit" disabled={submitting}>
          {submitting ? "Đang đăng nhập" : "Đăng nhập"}
        </button>
      </form>
    </main>
  );
}

function LanguageSwitcher({
  language,
  onChange
}: {
  language: LandingLanguage;
  onChange: (language: LandingLanguage) => void;
}) {
  return (
    <div className="language-switcher" aria-label="Select language">
      {languageOptions.map((option) => (
        <button
          className={option.value === language ? "language-option is-active" : "language-option"}
          type="button"
          onClick={() => onChange(option.value)}
          aria-label={option.name}
          aria-pressed={option.value === language}
          key={option.value}
        >
          <span className="language-flag" aria-hidden="true">{option.flag}</span>
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

function LandingPage() {
  const [language, setLanguage] = useState<LandingLanguage>("vi");
  const [event, setEvent] = useState<EventSettings>(fallbackEvent);
  const [sports, setSports] = useState<Sport[]>(fallbackSports);
  const [landingBuSummary, setLandingBuSummary] = useState<BuSummary[]>([]);
  const [matchResults, setMatchResults] = useState<Record<string, BadmintonMatch[]>>({});
  const [refreshingResults, setRefreshingResults] = useState(false);
  const [activeResultSportSlug, setActiveResultSportSlug] = useState<string | null>(null);
  const [activeScheduleSportSlug, setActiveScheduleSportSlug] = useState<string | null>(null);
  const [activeTeamSportSlug, setActiveTeamSportSlug] = useState<string | null>(null);
  const [landingMatchSearch, setLandingMatchSearch] = useState("");
  const [landingScheduleSearch, setLandingScheduleSearch] = useState("");
  const [showFootballRoster, setShowFootballRoster] = useState(false);
  const [showFootballPodium, setShowFootballPodium] = useState(true);
  const [showBadmintonPodium, setShowBadmintonPodium] = useState(true);
  const [loadingLanding, setLoadingLanding] = useState(true);
  const [footballTeams, setFootballTeams] = useState<FootballTeam[]>(fallbackFootballTeams);
  const [teamRosters, setTeamRosters] = useState<Record<string, BadmintonTeamsResponse>>({});
  const [loadingTeamRoster, setLoadingTeamRoster] = useState(false);
  const [stats, setStats] = useState<Stats>({
    participants: 0,
    lunch_count: 0,
    sports: []
  });

  const loadMatchResults = async (sportData: Sport[], showLoading = false) => {
    if (showLoading) setRefreshingResults(true);
    try {
      const teamSports = sportData.filter((sport) => isTeamManagedSportSlug(sport.slug));
      const entries = await Promise.all(
        teamSports.map(async (sport) => {
          try {
            const matches = await getBadmintonMatches(sport.slug);
            return [sport.slug, matches] as const;
          } catch {
            return [sport.slug, []] as const;
          }
        })
      );
      setMatchResults(Object.fromEntries(entries));
    } finally {
      if (showLoading) setRefreshingResults(false);
    }
  };

  const loadTeamRoster = async (sportSlug: string) => {
    if (teamRosters[sportSlug]) return;
    setLoadingTeamRoster(true);
    try {
      const roster = await getBadmintonTeams(sportSlug);
      setTeamRosters((current) => ({ ...current, [sportSlug]: roster }));
    } finally {
      setLoadingTeamRoster(false);
    }
  };

  useEffect(() => {
    Promise.all([getEvent(), getSports(), getStats(), getBuSummary(), getFootballRoster().catch(() => fallbackFootballTeams)])
      .then(([eventData, sportData, statsData, buSummaryData, footballRosterData]) => {
        const nextSports = sportData.length ? sportData : fallbackSports;
        if (eventData) setEvent(eventData);
        setSports(nextSports);
        setStats(statsData);
        setLandingBuSummary(buSummaryData);
        setFootballTeams(footballRosterData.length ? footballRosterData : fallbackFootballTeams);
        void loadMatchResults(nextSports);
      })
      .catch(() => {
        setEvent(fallbackEvent);
        setSports(fallbackSports);
        void loadMatchResults(fallbackSports);
      })
      .finally(() => {
        setLoadingLanding(false);
      });
  }, []);

  const sportCounts = new Map(stats.sports.map((sport) => [sport.id, sport.count]));
  const copy = landingCopy[language];
  const maxBuCount = Math.max(1, ...landingBuSummary.map((bu) => bu.count));
  const sportsWithResults = sports
    .filter((sport) => isTeamManagedSportSlug(sport.slug))
    .map((sport) => ({
      sport,
      matches: (matchResults[sport.slug] ?? []).filter(hasMatchScore)
    }))
    .filter((item) => item.matches.length);
  const activeResultBoard = sportsWithResults.find((item) => item.sport.slug === activeResultSportSlug) ?? null;
  const activeResultMatches = activeResultBoard ? filterMatchesByQuery(activeResultBoard.matches, landingMatchSearch) : [];
  const badmintonScheduleBoards = sports
    .filter((sport) => isBadmintonScheduleSportSlug(sport.slug))
    .map((sport) => ({
      sport,
      matches: matchResults[sport.slug] ?? []
    }))
    .filter((item) => item.matches.length);
  const activeScheduleSport = sports.find((sport) => sport.slug === activeScheduleSportSlug && isBadmintonScheduleSportSlug(sport.slug)) ?? null;
  const activeScheduleBoard = activeScheduleSport
    ? {
        sport: activeScheduleSport,
        matches: matchResults[activeScheduleSport.slug] ?? []
      }
    : null;
  const activeScheduleMatches = activeScheduleBoard ? filterMatchesByQuery(activeScheduleBoard.matches, landingScheduleSearch) : [];
  const activeTeamSport = sports.find((sport) => sport.slug === activeTeamSportSlug && isTeamManagedSportSlug(sport.slug)) ?? null;

  return (
    <main>
      <section className="hero">
        <div className="hero-overlay">
          <nav className="topbar">
            <a className="brand" href="/">
              <img src={logoSrc} alt="Super Cargo Service" />
            </a>
            <LanguageSwitcher language={language} onChange={setLanguage} />
          </nav>

          <div className="hero-content">
            <p className="eyebrow">Sport Day</p>
            <h1>{event.name}</h1>
            <div className="hero-facts">
              <span>
                <CalendarDays size={18} /> {formatDate(event.event_date, language)} · {formatTime(event.start_time)}-
                {formatTime(event.end_time)}
              </span>
              <a href={googleMapsUrl} target="_blank" rel="noreferrer">
                <MapPin size={18} /> {event.location_name}
              </a>
            </div>
          </div>
        </div>
      </section>

      {loadingLanding ? (
        <SkeletonMetricGrid count={3} className="stats-band" />
      ) : (
        <section className="stats-band">
          <div className="stat-tile">
            <Users size={22} />
            <strong>{stats.participants}</strong>
            <span>{copy.members}</span>
          </div>
          <div className="stat-tile">
            <Utensils size={22} />
            <strong>{stats.lunch_count}</strong>
            <span>{copy.lunch}</span>
          </div>
          <div className="stat-tile">
            <Trophy size={22} />
            <strong>{sports.length}</strong>
            <span>{copy.sports}</span>
          </div>
        </section>
      )}

      {showFootballPodium ? (
        <FootballPodiumSection language={language} onClose={() => setShowFootballPodium(false)} />
      ) : null}
      {showBadmintonPodium ? (
        <BadmintonPodiumSection language={language} onClose={() => setShowBadmintonPodium(false)} />
      ) : null}

      <section className="landing-bu-section sport-summary-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">{copy.buOverview}</p>
            <h2>{copy.membersByBu}</h2>
          </div>
          <Building2 size={28} />
        </div>
        <div className="bu-count-list">
          <div className="bu-count-row bu-count-head">
            <span>{copy.buName}</span>
            <span>{copy.count}</span>
          </div>
          {loadingLanding ? (
            <SkeletonRows count={5} />
          ) : (
            landingBuSummary.map((bu) => (
              <article className="bu-count-row" key={bu.name}>
                <strong>{bu.name}</strong>
                <div className="bu-count-value">
                  <span>{bu.count}</span>
                  <div className="sport-progress">
                    <i style={{ width: `${Math.max(8, (bu.count / maxBuCount) * 100)}%` }} />
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="content-section">
        <div className="section-heading">
          <p className="eyebrow">{copy.sportList}</p>
          <h2>{copy.sportHeading}</h2>
        </div>
        <div className="sport-grid">
          {loadingLanding ? <SkeletonCardGrid count={5} /> : sports.map((sport) => {
            const content = (
              <>
                <div className="sport-icon">
                  <Trophy size={22} />
                </div>
                <div>
                  <h3>{getLandingSportName(sport, language)}</h3>
                  <p>{getLandingSportCategory(sport, language)}</p>
                </div>
                <span>
                  {getDisplayedSportCount(sport, sportCounts)} {getLandingSportCountUnit(sport.slug, language)}
                </span>
              </>
            );

            return sport.slug === "football" ? (
              <button className="sport-card sport-card-button" type="button" onClick={() => setShowFootballRoster(true)} key={sport.id}>
                {content}
              </button>
            ) : isTeamManagedSportSlug(sport.slug) ? (
              <button
                className="sport-card sport-card-button"
                type="button"
                onClick={() => {
                  setActiveTeamSportSlug(sport.slug);
                  void loadTeamRoster(sport.slug);
                }}
                key={sport.id}
              >
                {content}
              </button>
            ) : (
              <article className="sport-card" key={sport.id}>
                {content}
              </article>
            );
          })}
        </div>
      </section>

      {showFootballRoster ? (
        <FootballRosterModal teams={footballTeams} language={language} onClose={() => setShowFootballRoster(false)} />
      ) : null}
      {activeTeamSport ? (
        <PublicTeamRosterModal
          sport={activeTeamSport}
          roster={teamRosters[activeTeamSport.slug]}
          isLoading={loadingTeamRoster}
          language={language}
          onClose={() => setActiveTeamSportSlug(null)}
          onShowSchedule={
            showLandingBadmintonSchedule && isBadmintonScheduleSportSlug(activeTeamSport.slug)
              ? () => {
                  setLandingScheduleSearch("");
                  setActiveScheduleSportSlug(activeTeamSport.slug);
                  setActiveTeamSportSlug(null);
                }
              : undefined
          }
        />
      ) : null}

      {showLandingBadmintonSchedule && (badmintonScheduleBoards.length || activeScheduleBoard) ? (
        <section className="landing-results-section landing-schedule-section">
          <div className="landing-results-title-row">
            <div className="section-heading">
              <p className="eyebrow">Lịch trận</p>
              <h2>Lịch thi đấu cầu lông</h2>
            </div>
            <button
              className="ghost-button landing-results-refresh"
              type="button"
              onClick={() => void loadMatchResults(sports.length ? sports : fallbackSports, true)}
              disabled={refreshingResults}
            >
              <RefreshCcw size={17} />
              {refreshingResults ? "Đang cập nhật" : "Cập nhật lịch"}
            </button>
          </div>
          <div className="landing-results-grid">
            {badmintonScheduleBoards.map(({ sport, matches }) => {
              const scheduledCount = matches.filter((match) => match.scheduled_court || match.scheduled_start || match.scheduled_end).length;
              return (
                <button
                  className="landing-result-board"
                  type="button"
                  onClick={() => {
                    setLandingScheduleSearch("");
                    setActiveScheduleSportSlug(sport.slug);
                  }}
                  key={sport.slug}
                >
                  <div className="landing-result-head">
                    <h3>{sport.name}</h3>
                    <span>{matches.length} trận</span>
                  </div>
                  <div className="landing-result-preview">
                    {matches.slice(0, 3).map((match) => (
                      <div className="landing-result-preview-row landing-schedule-preview-row" key={match.id}>
                        <span>{matchLabel(match)}</span>
                        <strong>{formatScheduleBadge(match)}</strong>
                      </div>
                    ))}
                    <small>{scheduledCount}/{matches.length} trận đã xếp sân</small>
                  </div>
                </button>
              );
            })}
          </div>

          {activeScheduleBoard ? (
            <div className="modal-backdrop" role="presentation" onClick={() => setActiveScheduleSportSlug(null)}>
              <section
                className="bracket-modal landing-results-modal landing-schedule-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Lịch trận ${activeScheduleBoard.sport.name}`}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="modal-head">
                  <div>
                    <p className="admin-kicker">Lịch trận</p>
                    <h2>{activeScheduleBoard.sport.name}</h2>
                  </div>
                  <button className="icon-only" type="button" onClick={() => setActiveScheduleSportSlug(null)} aria-label="Đóng modal">
                    <X size={18} />
                  </button>
                </div>
                <label className="search-box match-search-box">
                  <Search size={18} />
                  <input
                    value={landingScheduleSearch}
                    onChange={(event) => setLandingScheduleSearch(event.target.value)}
                    placeholder="Tìm tên đội, sân 1, 18:30, vòng loại, trận 3..."
                  />
                </label>
                <div className="landing-result-list">
                  {activeScheduleMatches.map((match) => (
                    <PublicScheduleMatch match={match} key={match.id} />
                  ))}
                  {!activeScheduleMatches.length ? (
                    <p className="empty-search">
                      {activeScheduleBoard.matches.length ? "Không tìm thấy trận phù hợp." : "Chưa có lịch trận cho môn này."}
                    </p>
                  ) : null}
                </div>
              </section>
            </div>
          ) : null}
        </section>
      ) : null}

      {showLandingBadmintonMatchResults && sportsWithResults.length ? (
        <section className="landing-results-section">
          <div className="landing-results-title-row">
            <div className="section-heading">
              <p className="eyebrow">Kết quả</p>
              <h2>Tỷ số các trận đấu</h2>
            </div>
            <button
              className="ghost-button landing-results-refresh"
              type="button"
              onClick={() => void loadMatchResults(sports.length ? sports : fallbackSports, true)}
              disabled={refreshingResults}
            >
              <RefreshCcw size={17} />
              {refreshingResults ? "Đang cập nhật" : "Cập nhật kết quả"}
            </button>
          </div>
          <div className="landing-results-grid">
            {sportsWithResults.map(({ sport, matches }) => (
              <button
                className="landing-result-board"
                type="button"
                onClick={() => {
                  setLandingMatchSearch("");
                  setActiveResultSportSlug(sport.slug);
                }}
                key={sport.slug}
              >
                <div className="landing-result-head">
                  <h3>{sport.name}</h3>
                  <span>{matches.length} trận</span>
                </div>
                <div className="landing-result-preview">
                  {matches.slice(0, 3).map((match) => (
                    <div className="landing-result-preview-row" key={match.id}>
                      <span>{matchLabel(match)}</span>
                      <strong>{formatSetScoreResult(match)}</strong>
                    </div>
                  ))}
                  {matches.length > 3 ? <small>+{matches.length - 3} trận khác</small> : null}
                </div>
              </button>
            ))}
          </div>

          {activeResultBoard ? (
            <div className="modal-backdrop" role="presentation" onClick={() => setActiveResultSportSlug(null)}>
              <section
                className="bracket-modal landing-results-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Kết quả ${activeResultBoard.sport.name}`}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="modal-head">
                  <div>
                    <p className="admin-kicker">Kết quả</p>
                    <h2>{activeResultBoard.sport.name}</h2>
                  </div>
                  <button className="icon-only" type="button" onClick={() => setActiveResultSportSlug(null)} aria-label="Đóng modal">
                    <X size={18} />
                  </button>
                </div>
                <label className="search-box match-search-box">
                  <Search size={18} />
                  <input
                    value={landingMatchSearch}
                    onChange={(event) => setLandingMatchSearch(event.target.value)}
                    placeholder="Tìm tên đội, vòng loại, bán kết, trận 3..."
                  />
                </label>
                <div className="landing-result-list">
                  {activeResultMatches.map((match) => (
                    <PublicMatchResult match={match} key={match.id} />
                  ))}
                  {!activeResultMatches.length ? <p className="empty-search">Không tìm thấy trận phù hợp.</p> : null}
                </div>
              </section>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="event-band">
        <div>
          <p className="eyebrow">{copy.info}</p>
          <h2>{event.location_name}</h2>
          <p>{event.location_address}</p>
        </div>
        <div className="event-actions">
          <a className="secondary-button" href={googleMapsUrl} target="_blank" rel="noreferrer">
            <MapPin size={18} />
            {copy.viewMap}
          </a>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function BadmintonPodiumSection({ language, onClose }: { language: LandingLanguage; onClose: () => void }) {
  const copy = landingCopy[language];

  return (
    <section className="badminton-podium-section" aria-labelledby="badminton-podium-title">
      <div className="badminton-podium-head">
        <div>
          <p className="eyebrow">{copy.recognition}</p>
          <h2 id="badminton-podium-title">{copy.badmintonResults}</h2>
        </div>
        <div className="badminton-podium-actions">
          <div className="badminton-podium-badge">
            <Trophy size={19} />
            {copy.topBadminton}
          </div>
          <button className="podium-close-button" type="button" onClick={onClose} aria-label={copy.closeBadmintonResults}>
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="badminton-podium-grid">
        {badmintonPodiumResults.map((division) => {
          const divisionTitle = division.title[language];
          const divisionLabel = division.label[language];
          return (
          <section className="podium-board" key={divisionTitle}>
            <div className="podium-board-head">
              <span>{divisionLabel}</span>
              <h3>{divisionTitle}</h3>
            </div>
            <div className="podium-list">
              {division.winners.map((winner) => (
                <article className={`podium-place rank-${winner.rank}`} key={`${divisionTitle}-${winner.rank}`}>
                  <div className="podium-rank">
                    {winner.rank === 1 ? <Trophy size={24} /> : <Medal size={22} />}
                    <strong>{copy.rank} {winner.rank}</strong>
                  </div>
                  <div className="podium-team">
                    {winner.names.map((name) => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                  <small className="podium-bu">{copy.bu}: {winner.bu}</small>
                </article>
              ))}
            </div>
          </section>
          );
        })}
      </div>
    </section>
  );
}

function FootballPodiumSection({ language, onClose }: { language: LandingLanguage; onClose: () => void }) {
  const copy = landingCopy[language];

  return (
    <section className="badminton-podium-section football-podium-section" aria-labelledby="football-podium-title">
      <div className="badminton-podium-head">
        <div>
          <p className="eyebrow">{copy.recognition}</p>
          <h2 id="football-podium-title">{copy.footballResults}</h2>
        </div>
        <div className="badminton-podium-actions">
          <div className="badminton-podium-badge">
            <Trophy size={19} />
            {copy.championRunnerUp}
          </div>
          <button className="podium-close-button" type="button" onClick={onClose} aria-label={copy.closeFootballResults}>
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="football-final-score" aria-label={`${copy.finalScore}: SCT 4 - 2 SCS`}>
        <span>{copy.finalScore}</span>
        <div className="football-final-scoreline">
          <strong>{footballFinalScore.championTeam}</strong>
          <b>{footballFinalScore.championScore}</b>
          <i>-</i>
          <b>{footballFinalScore.runnerUpScore}</b>
          <strong>{footballFinalScore.runnerUpTeam}</strong>
        </div>
      </div>
      <div className="podium-list football-podium-list">
        {footballPodiumResults.map((result) => (
          <article className={`podium-place rank-${result.rank}`} key={result.team}>
            <div className="podium-rank">
              {result.rank === 1 ? <Trophy size={24} /> : <Medal size={22} />}
              <strong>{result.rank === 1 ? copy.champion : copy.runnerUp}</strong>
            </div>
            <div className="podium-team">
              <span>{result.team}</span>
            </div>
            <small className="podium-bu">{copy.football}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function PublicMatchResult({ match }: { match: BadmintonMatch }) {
  return (
    <article className="public-match-result">
      <div className="public-match-title">
        <strong>{matchLabel(match)}</strong>
        <span>{formatSetScoreResult(match)}</span>
      </div>
      <div className="public-match-scoreline">
        <span className={match.winner_team_id === match.team_one_id ? "is-winner" : ""}>
          {displayMatchTeamLabel(match, "one")}
        </span>
        <strong>{displayMatchTeamScore(match, "one")}</strong>
        <i>-</i>
        <strong>{displayMatchTeamScore(match, "two")}</strong>
        <span className={match.winner_team_id === match.team_two_id ? "is-winner" : ""}>
          {displayMatchTeamLabel(match, "two")}
        </span>
      </div>
      <small>{match.winner_team_name ? `Đội thắng: ${displayWinnerTeamLabel(match)}` : formatMatchScoreDetail(match)}</small>
    </article>
  );
}

function PublicScheduleMatch({ match }: { match: BadmintonMatch }) {
  return (
    <article className={match.is_done ? "public-match-result public-schedule-match is-done" : "public-match-result public-schedule-match"}>
      <div className="public-match-title">
        <strong>{matchLabel(match)}</strong>
        <span>{match.is_done ? "Done" : "Chưa xong"}</span>
      </div>
      <div className="public-schedule-meta">
        <span>
          <Clock size={16} />
          {formatScheduleBadge(match)}
        </span>
        <span>
          <Trophy size={16} />
          {formatSetScoreResult(match)}
        </span>
      </div>
      <div className="public-schedule-teams">
        <span className={match.winner_team_id === match.team_one_id ? "is-winner" : ""}>{displayMatchTeamLabel(match, "one")}</span>
        <i>vs</i>
        <span className={match.winner_team_id === match.team_two_id ? "is-winner" : ""}>{displayMatchTeamLabel(match, "two")}</span>
      </div>
    </article>
  );
}

function PublicTeamRosterModal({
  sport,
  roster,
  isLoading,
  language,
  onClose,
  onShowSchedule
}: {
  sport: Sport;
  roster?: BadmintonTeamsResponse;
  isLoading: boolean;
  language: LandingLanguage;
  onClose: () => void;
  onShowSchedule?: () => void;
}) {
  const copy = landingCopy[language];
  const teams = roster?.teams ?? [];
  const groupedTeams = {
    A: teams.filter((team) => team.group_name === "A"),
    B: teams.filter((team) => team.group_name === "B"),
    none: teams.filter((team) => team.group_name !== "A" && team.group_name !== "B")
  };
  const hasGroupedTeams = groupedTeams.A.length || groupedTeams.B.length;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="bracket-modal public-team-roster-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${copy.teamRoster} ${getLandingSportName(sport, language)}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <p className="admin-kicker">{copy.teamRoster}</p>
            <h2>{getLandingSportName(sport, language)}</h2>
          </div>
          <button className="icon-only" type="button" onClick={onClose} aria-label={copy.closeModal}>
            <X size={18} />
          </button>
        </div>
        {onShowSchedule ? (
          <button className="ghost-button public-team-schedule-button" type="button" onClick={onShowSchedule}>
            <Clock size={17} />
            {copy.viewSchedule}
          </button>
        ) : null}
        {isLoading && !roster ? (
          <p className="empty-search">{copy.loadingTeams}</p>
        ) : (
          <div className="public-team-groups">
            {hasGroupedTeams ? (
              <>
                <PublicTeamGroup groupName="A" teams={groupedTeams.A} language={language} />
                <PublicTeamGroup groupName="B" teams={groupedTeams.B} language={language} />
                {groupedTeams.none.length ? <PublicTeamGroup groupName={null} teams={groupedTeams.none} language={language} /> : null}
              </>
            ) : (
              <PublicTeamGroup groupName={null} teams={teams} language={language} />
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function PublicTeamGroup({
  groupName,
  teams,
  language
}: {
  groupName: BadmintonGroupName | null;
  teams: BadmintonTeam[];
  language: LandingLanguage;
}) {
  const copy = landingCopy[language];
  const groupClass = groupName ? `group-${groupName.toLowerCase()}` : "group-none";

  return (
    <section className={`public-team-group ${groupClass}`}>
      <div className="public-team-group-head">
        <h3>{groupName ? `${copy.group} ${groupName}` : copy.ungrouped}</h3>
        <span>{teams.length} {copy.teamUnit}</span>
      </div>
      <div className="public-team-list">
        {teams.length ? (
          teams.map((team) => (
            <article className="public-team-card" key={team.id}>
              <strong>{displayTeamName(team)}</strong>
              <span>{[team.player_one_name, team.player_two_name].filter(Boolean).join(" / ") || copy.noMembers}</span>
            </article>
          ))
        ) : (
          <p className="empty-bracket">{copy.noTeams}</p>
        )}
      </div>
    </section>
  );
}

function FootballRosterModal({
  teams,
  language = "vi",
  onClose
}: {
  teams: FootballTeam[];
  language?: LandingLanguage;
  onClose: () => void;
}) {
  const copy = landingCopy[language];

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="bracket-modal football-roster-modal"
        role="dialog"
        aria-modal="true"
        aria-label={copy.footballRosterTitle}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <p className="admin-kicker">{copy.football}</p>
            <h2>{copy.footballRosterTitle}</h2>
          </div>
          <button className="icon-only" type="button" onClick={onClose} aria-label={copy.closeModal}>
            <X size={18} />
          </button>
        </div>
        <div className="football-team-list">
          {teams.map((team) => (
            <section className="football-team-section" key={team.name}>
              <div className="football-team-head">
                <h3>{team.name}</h3>
                <span>{team.players.length} cầu thủ</span>
              </div>
              <div className="football-roster-grid">
                {team.players.map((player) => (
                  <article className="football-player-card" key={`${team.name}-${player.order}-${player.name}`}>
                    <span>{player.order}</span>
                    <strong>{player.name}</strong>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}

function AdminPage() {
  const [event, setEvent] = useState<EventSettings>(fallbackEvent);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [sports, setSports] = useState<Sport[]>(fallbackSports);
  const [stats, setStats] = useState<Stats>({ participants: 0, lunch_count: 0, sports: [] });
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [buFilter, setBuFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [sportFilter, setSportFilter] = useState("all");
  const [lunchFilter, setLunchFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [syncSheetUrl, setSyncSheetUrl] = useState(fallbackEvent.sync_sheet_url);
  const [savingSyncLink, setSavingSyncLink] = useState(false);
  const [showFootballRoster, setShowFootballRoster] = useState(false);
  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [footballTeams, setFootballTeams] = useState<FootballTeam[]>(fallbackFootballTeams);

  const load = async (showLoading = false) => {
    if (showLoading) setLoadingAdmin(true);
    try {
      const [eventData, sportData, participantData, statsData, footballRosterData] = await Promise.all([
        getEvent(),
        getSports(),
        getParticipants(),
        getStats(),
        getFootballRoster().catch(() => fallbackFootballTeams)
      ]);
      if (eventData) {
        setEvent(eventData);
        setSyncSheetUrl(eventData.sync_sheet_url ?? fallbackEvent.sync_sheet_url);
      }
      setSports(sportData);
      setParticipants(participantData);
      setStats(statsData);
      setFootballTeams(footballRosterData.length ? footballRosterData : fallbackFootballTeams);
    } finally {
      if (showLoading) setLoadingAdmin(false);
    }
  };

  useEffect(() => {
    load(true).catch((error) => setMessage(error.message));
  }, []);

  const buOptions = unique(participants.map((participant) => participant.bu).filter(Boolean));
  const departmentOptions = unique(participants.map((participant) => participant.department).filter(Boolean));

  const filteredParticipants = useMemo(() => {
    return participants.filter((participant) => {
      const haystack = [
        participant.bu,
        participant.department,
        participant.english_name,
        participant.full_name,
        participant.phone,
        participant.relationship
      ]
        .join(" ")
        .toLowerCase();
      const matchesSearch = !query || haystack.includes(query.toLowerCase());
      const matchesBu = buFilter === "all" || participant.bu === buFilter;
      const matchesDepartment = departmentFilter === "all" || participant.department === departmentFilter;
      const matchesSport =
        sportFilter === "all" || participant.registrations.some((registration) => String(registration.sport_id) === sportFilter);
      const matchesLunch =
        lunchFilter === "all" || (lunchFilter === "yes" ? participant.lunch : !participant.lunch);
      const matchesGender = genderFilter === "all" || participant.gender === genderFilter;

      return matchesSearch && matchesBu && matchesDepartment && matchesSport && matchesLunch && matchesGender;
    });
  }, [buFilter, departmentFilter, genderFilter, lunchFilter, participants, query, sportFilter]);

  const syncSheet = async () => {
    setSyncing(true);
    setMessage("Đang đồng bộ dữ liệu từ Google Sheet...");
    try {
      const result = await importSheet();
      await load();
      setMessage(
        `Đã đồng bộ ${result.imported_participants} thành viên và ${result.imported_registrations} lượt đăng ký từ Google Sheet.`
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không đồng bộ được Google Sheet.");
    } finally {
      setSyncing(false);
    }
  };

  const saveSyncLink = async () => {
    setSavingSyncLink(true);
    setMessage("Đang lưu link đồng bộ Sheet...");
    try {
      const updatedEvent = await updateEvent({ ...event, sync_sheet_url: syncSheetUrl });
      setEvent(updatedEvent);
      setSyncSheetUrl(updatedEvent.sync_sheet_url);
      setMessage("Đã lưu link đồng bộ Sheet.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không lưu được link đồng bộ Sheet.");
    } finally {
      setSavingSyncLink(false);
    }
  };

  const displayedSportStats = stats.sports.map((sport) => ({ ...sport, count: getDisplayedSportCount(sport) }));
  const maxSportCount = Math.max(1, ...displayedSportStats.map((sport) => sport.count));
  const buSummary = useMemo(() => countBy(participants.map((participant) => participant.bu || "Chưa có BU")), [participants]);
  const maxBuCount = Math.max(1, ...buSummary.map((bu) => bu.count));

  return (
    <main className="admin-shell">
      <header className="admin-hero">
        <div className="admin-hero-top">
          <a className="brand dark" href="/">
            <img src={logoSrc} alt="Super Cargo Service" />
          </a>
          <div className="admin-actions">
            <a className="ghost-button" href="/" target="_blank" rel="noreferrer">
              Landing page
              <ExternalLink size={16} />
            </a>
            <button className="icon-button sync-button" type="button" onClick={syncSheet} disabled={syncing}>
              <RefreshCcw size={18} />
              {syncing ? "Đang đồng bộ" : "Đồng bộ Sheet"}
            </button>
          </div>
        </div>

        <div className="admin-hero-content">
          <p className="admin-kicker">Sport Day Control Center</p>
          <h1>Quản lý danh sách tham dự</h1>
          <p>
            Dữ liệu thành viên và môn đăng ký được đồng bộ trực tiếp từ Google Sheet. Admin dùng màn hình này để theo
            dõi, lọc và kiểm tra số liệu mới nhất.
          </p>
          <div className="admin-meta">
            <span>
              <CalendarDays size={18} />
              {formatDate(event.event_date)} · {formatTime(event.start_time)}-{formatTime(event.end_time)}
            </span>
            <a href={googleMapsUrl} target="_blank" rel="noreferrer">
              <MapPin size={18} />
              {event.location_name}
            </a>
          </div>
        </div>
      </header>

      {loadingAdmin ? (
        <SkeletonMetricGrid count={7} className="admin-stats" />
      ) : (
        <section className="admin-stats">
          <Metric icon={<Users size={20} />} label="Thành viên" value={stats.participants} />
          <Metric icon={<Utensils size={20} />} label="Ăn trưa" value={stats.lunch_count} />
          {displayedSportStats.map((sport) => (
            <Metric key={sport.id} icon={<Trophy size={20} />} label={participantSportLabel(sport.name)} value={sport.count} />
          ))}
        </section>
      )}

      {message ? <div className="notice">{message}</div> : null}

      <section className="sport-summary-panel sync-settings-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">Google Sheet</p>
            <h2>Link đồng bộ</h2>
          </div>
          <ClipboardList size={28} />
        </div>
        <div className="sync-settings-form">
          <label className="field">
            Link đồng bộ Sheet
            <input
              value={syncSheetUrl}
              onChange={(event) => setSyncSheetUrl(event.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/.../edit?gid=0"
            />
          </label>
          <button className="primary-action" type="button" onClick={saveSyncLink} disabled={savingSyncLink}>
            <Save size={18} />
            {savingSyncLink ? "Đang lưu" : "Lưu link"}
          </button>
        </div>
      </section>

      <section className="sport-summary-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">Tổng quan BU</p>
            <h2>Thành viên theo BU</h2>
          </div>
          <Building2 size={28} />
        </div>
        <div className="bu-count-list">
          <div className="bu-count-row bu-count-head">
            <span>Tên BU</span>
            <span>Số lượng</span>
          </div>
          {loadingAdmin ? (
            <SkeletonRows count={6} />
          ) : (
            buSummary.map((bu) => (
              <article className="bu-count-row" key={bu.name}>
                <strong>{bu.name}</strong>
                <div className="bu-count-value">
                  <span>{bu.count}</span>
                  <div className="sport-progress">
                    <i style={{ width: `${Math.max(8, (bu.count / maxBuCount) * 100)}%` }} />
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="sport-summary-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">Tổng quan môn thi</p>
            <h2>Phân bổ đăng ký</h2>
          </div>
          <BarChart3 size={28} />
        </div>
        <div className="sport-summary-grid">
          {loadingAdmin ? <SkeletonCardGrid count={5} /> : displayedSportStats.map((sport) => {
            const content = (
              <>
                <div className="sport-summary-top">
                  <strong>{participantSportLabel(sport.name)}</strong>
                  <span>{sport.count}</span>
                </div>
                <div className="sport-progress">
                  <span style={{ width: `${Math.max(8, (sport.count / maxSportCount) * 100)}%` }} />
                </div>
                {isTeamManagedSportSlug(sport.slug) ? <small>Quản lý trận đấu</small> : null}
              </>
            );

            return isTeamManagedSportSlug(sport.slug) ? (
              <a className="sport-summary-card sport-summary-link" href={`/admin/badminton/${sport.slug}`} key={sport.id}>
                {content}
              </a>
            ) : sport.slug === "football" ? (
              <button
                className="sport-summary-card sport-summary-link sport-summary-button"
                type="button"
                onClick={() => setShowFootballRoster(true)}
                key={sport.id}
              >
                {content}
              </button>
            ) : (
              <article className="sport-summary-card" key={sport.id}>
                {content}
              </article>
            );
          })}
        </div>
      </section>

      {showFootballRoster ? <FootballRosterModal teams={footballTeams} onClose={() => setShowFootballRoster(false)} /> : null}

      <section className="panel table-panel">
        <div className="table-toolbar">
          <div>
            <p className="admin-kicker">Danh sách từ Google Sheet</p>
            <h2>{filteredParticipants.length} thành viên</h2>
          </div>
          <div className="filters">
            <label className="search-box">
              <Search size={18} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Tìm kiếm" />
            </label>
            <select value={buFilter} onChange={(event) => setBuFilter(event.target.value)}>
              <option value="all">Tất cả BU</option>
              {buOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)}>
              <option value="all">Tất cả phòng ban</option>
              {departmentOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select value={sportFilter} onChange={(event) => setSportFilter(event.target.value)}>
              <option value="all">Tất cả môn</option>
              {sports.map((sport) => (
                <option key={sport.id} value={sport.id}>
                  {sport.name}
                </option>
              ))}
            </select>
            <select value={lunchFilter} onChange={(event) => setLunchFilter(event.target.value)}>
              <option value="all">Ăn trưa</option>
              <option value="yes">Có</option>
              <option value="no">Không</option>
            </select>
            <select value={genderFilter} onChange={(event) => setGenderFilter(event.target.value)}>
              <option value="all">Giới tính</option>
              <option value="male">Nam</option>
              <option value="female">Nữ</option>
            </select>
          </div>
        </div>

        <div className="table-wrap participant-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Thành viên</th>
                <th>BU</th>
                <th>Phòng ban</th>
                <th>Tuổi/Giới tính</th>
                <th>Ăn trưa</th>
                <th>Môn đăng ký</th>
              </tr>
            </thead>
            <tbody>
              {loadingAdmin ? <SkeletonTableRows rows={8} columns={6} /> : filteredParticipants.map((participant) => (
                <tr key={participant.id}>
                  <td>
                    <strong>{participant.full_name}</strong>
                    <span>{participant.english_name || "-"}</span>
                    {participant.phone ? <small>{participant.phone}</small> : null}
                  </td>
                  <td>{participant.bu}</td>
                  <td>{participant.department}</td>
                  <td>
                    {[participant.age, genderLabel(participant.gender)].filter(Boolean).join(" / ") || "-"}
                  </td>
                  <td>{participant.lunch ? "Có" : "Không"}</td>
                  <td>
                    <div className="chips">
                      {participant.registrations.length ? (
                        participant.registrations.map((registration) => (
                          <span className="chip" key={registration.id}>
                            {registration.sport_name}
                            {registration.teammate_name ? <small>{registration.teammate_name}</small> : null}
                          </span>
                        ))
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <Footer />
    </main>
  );
}

function BadmintonAdminPage({ sportSlug }: { sportSlug: string }) {
  const validSport = isTeamManagedSportSlug(sportSlug);
  const isFoosball = sportSlug === "foosball-doubles";
  const [sport, setSport] = useState<Sport | null>(fallbackSports.find((item) => item.slug === sportSlug) ?? null);
  const [teams, setTeams] = useState<BadmintonTeam[]>([]);
  const [matches, setMatches] = useState<BadmintonMatch[]>([]);
  const [teamDraft, setTeamDraft] = useState<BadmintonTeamDraft>(emptyTeamDraft());
  const [matchDraft, setMatchDraft] = useState<BadmintonMatchDraft>(() => emptyMatchDraft(sportSlug));
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editingMatchId, setEditingMatchId] = useState<number | null>(null);
  const [confirmingDeleteMatchId, setConfirmingDeleteMatchId] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [matchSearch, setMatchSearch] = useState("");
  const [importingBadmintonSheet, setImportingBadmintonSheet] = useState(false);
  const [generatingGroupMatches, setGeneratingGroupMatches] = useState(false);
  const [showMatchGuide, setShowMatchGuide] = useState(false);
  const [loadingTournament, setLoadingTournament] = useState(true);
  const matchFormRef = useRef<HTMLDivElement | null>(null);

  const load = async (showLoading = false) => {
    if (!validSport) return;
    if (showLoading) setLoadingTournament(true);
    try {
      const [sportData, teamData, matchData] = await Promise.all([
        getSports(),
        getBadmintonTeams(sportSlug),
        getBadmintonMatches(sportSlug)
      ]);
      setSport(sportData.find((item) => item.slug === sportSlug) ?? null);
      setTeams(teamData.teams);
      setMatches(matchData);
    } finally {
      if (showLoading) setLoadingTournament(false);
    }
  };

  useEffect(() => {
    load(true).catch((error) => setMessage(error.message));
  }, [sportSlug, validSport]);

  useEffect(() => {
    if (!message) return undefined;
    const timeoutId = window.setTimeout(() => setMessage(""), 3200);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  if (!validSport) {
    return (
      <main className="admin-shell">
        <section className="panel">
          <a className="ghost-button compact-button" href="/admin">
            <ArrowLeft size={16} />
            Quay lại admin
          </a>
          <h1>Không tìm thấy môn thi đấu</h1>
        </section>
      </main>
    );
  }

  const syncBadmintonSheet = async () => {
    const sheetTab = getTeamSheetTabName(sportSlug);
    setImportingBadmintonSheet(true);
    setMessage(`Đang đọc tab ${sheetTab} và tạo bảng/đội...`);
    try {
      const result = await importBadmintonSheet(sportSlug);
      await load();
      setMessage(`Đã tạo ${result.imported_groups} bảng và ${result.imported_teams} đội từ tab ${sheetTab}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `Không đọc được tab ${sheetTab}.`);
    } finally {
      setImportingBadmintonSheet(false);
    }
  };

  const removeTeam = async (team: BadmintonTeam) => {
    if (!window.confirm(`Xác nhận xóa đội "${team.name}"? Các trận liên quan cũng sẽ bị xóa.`)) return;
    try {
      await deleteBadmintonTeam(team.id);
      setMessage("Đã xóa đội.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không xóa được đội.");
    }
  };

  const editTeam = (team: BadmintonTeam) => {
    setEditingTeamId(team.id);
    setTeamDraft({
      name: displayTeamName(team),
      player_one_name: team.player_one_name,
      player_two_name: team.player_two_name,
      group_name: team.group_name,
      source_registration_id: team.source_registration_id,
      source_type: team.source_type
    });
  };

  const saveTeam = async () => {
    if (!editingTeamId) return;
    try {
      await updateBadmintonTeam(editingTeamId, teamDraft);
      setEditingTeamId(null);
      setTeamDraft(emptyTeamDraft());
      setMessage("Đã cập nhật đội.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không lưu được đội.");
    }
  };

  const saveMatch = async () => {
    const draftToSave = normalizeMatchDraftForSport(matchDraft, sportSlug);
    try {
      if (editingMatchId) {
        await updateBadmintonMatch(editingMatchId, { ...draftToSave, winner_team_id: null });
        setMessage("Đã cập nhật trận đấu.");
      } else {
        await createBadmintonMatch(sportSlug, { ...draftToSave, winner_team_id: null });
        setMessage("Đã tạo trận đấu.");
      }
      setMatchDraft(emptyMatchDraft(sportSlug));
      setEditingMatchId(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không lưu được trận đấu.");
    }
  };

  const generateGroupMatches = async () => {
    const groups: BadmintonGroupName[] = ["A", "B"];
    const incompleteGroups = groups.filter((group) => teams.filter((team) => team.group_name === group).length < 2);
    if (incompleteGroups.length) {
      setMessage(`Cần ít nhất 2 đội ở ${incompleteGroups.map((group) => `bảng ${group}`).join(", ")} trước khi random.`);
      return;
    }

    setGeneratingGroupMatches(true);
    setMessage("Đang random xếp trận vòng loại...");
    try {
      let createdCount = 0;

      for (const group of groups) {
        const groupTeams = teams.filter((team) => team.group_name === group);
        const groupMatches = matches.filter((match) => match.round_type === "group" && match.group_name === group);
        const existingPairs = new Set(groupMatches.map((match) => teamPairKey(match.team_one_id, match.team_two_id)));
        const missingPairs = shuffleItems(getRoundRobinPairs(groupTeams).filter(([teamOne, teamTwo]) => !existingPairs.has(teamPairKey(teamOne.id, teamTwo.id))));
        const openMatchNumbers = getMatchNumberOptions("group", groupTeams.length).filter(
          (number) => !isMatchNumberUsed(matches, "group", group, number, null)
        );

        for (let index = 0; index < Math.min(missingPairs.length, openMatchNumbers.length); index += 1) {
          const [firstTeam, secondTeam] = missingPairs[index];
          const [teamOne, teamTwo] = Math.random() > 0.5 ? [secondTeam, firstTeam] : [firstTeam, secondTeam];
          await createBadmintonMatch(sportSlug, {
            round_type: "group",
            group_name: group,
            match_number: openMatchNumbers[index],
            winning_score: getDefaultWinningScore("group", sportSlug),
            team_one_id: teamOne.id,
            team_two_id: teamTwo.id,
            team_one_score: 0,
            team_two_score: 0,
            set_scores: [],
            winner_team_id: null,
            scheduled_court: null,
            scheduled_start: "",
            scheduled_end: "",
            is_done: false,
            notes: ""
          });
          createdCount += 1;
        }
      }

      await load();
      setEditingMatchId(null);
      setMatchDraft(emptyMatchDraft(sportSlug));
      setMessage(createdCount ? `Đã random tạo ${createdCount} trận vòng loại còn thiếu.` : "Các trận vòng loại đã được tạo đủ.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không random xếp trận được.");
    } finally {
      setGeneratingGroupMatches(false);
    }
  };

  const removeMatch = async (match: BadmintonMatch) => {
    try {
      await deleteBadmintonMatch(match.id);
      setConfirmingDeleteMatchId(null);
      setMessage("Đã xóa trận đấu.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không xóa được trận đấu.");
    }
  };

  const editMatch = (match: BadmintonMatch) => {
    setEditingMatchId(match.id);
    setConfirmingDeleteMatchId(null);
    setMatchDraft(normalizeMatchDraftForSport(toMatchDraft(match), sportSlug));
  };

  const closeMatchEditor = () => {
    setEditingMatchId(null);
    setMatchDraft(emptyMatchDraft(sportSlug));
  };

  const draftWinnerName = getDraftWinnerName(matchDraft, teams);
  const matchNumberOptions = getMatchNumberOptions(matchDraft.round_type, getGroupTeamCount(teams, matchDraft.group_name));
  const availableMatchNumbers = matchNumberOptions.filter(
    (number) => !isMatchNumberUsed(matches, matchDraft.round_type, matchDraft.group_name, number, editingMatchId)
  );
  const matchTeamOptions = getMatchTeamOptions(teams, matchDraft);
  const groupedTeams = {
    A: teams.filter((team) => team.group_name === "A"),
    B: teams.filter((team) => team.group_name === "B"),
    none: teams.filter((team) => team.group_name !== "A" && team.group_name !== "B")
  };
  const filteredMatches = filterMatchesByQuery(matches, matchSearch);

  return (
    <main className="admin-shell badminton-shell">
      <header className="admin-hero badminton-hero">
        <div className="admin-hero-top">
          <a className="brand dark" href="/">
            <img src={logoSrc} alt="Super Cargo Service" />
          </a>
          <div className="admin-actions">
            <button className="ghost-button" type="button" onClick={() => setShowMatchGuide(true)}>
              <ClipboardList size={16} />
              Hướng dẫn
            </button>
            <button className="icon-button sync-button" type="button" onClick={syncBadmintonSheet} disabled={importingBadmintonSheet}>
              <RefreshCcw size={18} />
              {importingBadmintonSheet ? "Đang đọc tab" : "Đọc bảng & đội"}
            </button>
            <a className="ghost-button" href="/admin">
              <ArrowLeft size={16} />
              Quay lại admin
            </a>
          </div>
        </div>
        <div className="admin-hero-content">
          <p className="admin-kicker">Sport Match Manager</p>
          <h1>{sport?.name ?? "Cầu lông đôi"}</h1>
          <p>Đọc đội từ tab {getTeamSheetTabName(sportSlug)}, chia bảng A/B và sắp xếp trận đấu khi cần.</p>
        </div>
      </header>

      {message ? <div className="toast-message">{message}</div> : null}
      {showMatchGuide ? <MatchManagementGuideModal sportName={sport?.name ?? "môn thi đấu"} isFoosball={isFoosball} onClose={() => setShowMatchGuide(false)} /> : null}

      <section className="sport-summary-panel tournament-rules-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">Thể thức thi đấu</p>
            <h2>Vòng loại, bán kết chéo và chung kết</h2>
          </div>
          <ClipboardList size={26} />
        </div>
        <div className="rules-grid">
          <article>
            <h3>Vòng loại</h3>
            <p>Đội được chia 2 bảng A/B từ tab {getTeamSheetTabName(sportSlug)}.</p>
            <ul>
              <li>Import chỉ tạo bảng và đội; trận đấu được tạo thủ công khi cần xếp lịch.</li>
              <li>Đánh xoay vòng tính điểm vòng loại, mỗi trận 1 game {isFoosball ? 10 : 15} điểm.</li>
              <li>Thắng được 1 điểm, thua 0 điểm. Nếu hòa điểm thì xếp hạng theo hiệu số, sau đó đến tổng điểm ghi được.</li>
            </ul>
          </article>
          <article>
            <h3>Bán kết chéo và chung kết</h3>
            <p>Chọn ra 2 cặp nhất và nhì mỗi bảng để vào vòng đấu chéo.</p>
            <ul>
              <li>Cặp nhất bảng A gặp cặp nhì bảng B.</li>
              <li>Cặp nhì bảng A gặp cặp nhất bảng B.</li>
              <li>
                {isFoosball
                  ? "Bán kết đánh 3 set, mỗi set 10 điểm."
                  : "Bán kết sẽ hội ý đánh 3 set 15 điểm hay 21 điểm tùy khả năng lúc đó của các đội."}
              </li>
              <li>{isFoosball ? "Chung kết đánh 3 set, mỗi set 10 điểm." : "Chung kết đánh 3 set 21 điểm."}</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="badminton-grid">
        <div className="sport-summary-panel team-roster-panel">
          <div className="section-title-row">
            <div>
              <p className="admin-kicker">Danh sách đội</p>
              <h2>Đội theo bảng</h2>
            </div>
            <Users size={26} />
          </div>

          {loadingTournament ? (
            <SkeletonTeamRoster />
          ) : (
            <div className="team-roster-grid">
              <TeamGroupList groupName="A" teams={groupedTeams.A} onEditTeam={editTeam} onRemoveTeam={removeTeam} />
              <TeamGroupList groupName="B" teams={groupedTeams.B} onEditTeam={editTeam} onRemoveTeam={removeTeam} />
              {groupedTeams.none.length ? (
                <TeamGroupList groupName={null} teams={groupedTeams.none} onEditTeam={editTeam} onRemoveTeam={removeTeam} />
              ) : null}
            </div>
          )}

          {editingTeamId ? (
            <div className="team-edit-form">
              <select
                value={teamDraft.group_name ?? ""}
                onChange={(event) =>
                  setTeamDraft({
                    ...teamDraft,
                    group_name: event.target.value === "A" || event.target.value === "B" ? event.target.value : null
                  })
                }
              >
                <option value="">Chưa xếp bảng</option>
                <option value="A">Bảng A</option>
                <option value="B">Bảng B</option>
              </select>
              <input
                value={teamDraft.name}
                onChange={(event) => setTeamDraft({ ...teamDraft, name: event.target.value })}
                placeholder="Tên đội"
              />
              <input
                value={teamDraft.player_one_name}
                onChange={(event) => setTeamDraft({ ...teamDraft, player_one_name: event.target.value })}
                placeholder="Thành viên 1"
              />
              <input
                value={teamDraft.player_two_name}
                onChange={(event) => setTeamDraft({ ...teamDraft, player_two_name: event.target.value })}
                placeholder="Thành viên 2"
              />
              <button className="primary-action" type="button" onClick={saveTeam}>
                <Save size={18} />
                Lưu đội
              </button>
              <button
                className="ghost-button"
                type="button"
                onClick={() => {
                  setEditingTeamId(null);
                  setTeamDraft(emptyTeamDraft());
                }}
              >
                Hủy
              </button>
            </div>
          ) : null}
        </div>

        {editingMatchId ? <div className="modal-backdrop match-editor-backdrop" role="presentation" onClick={closeMatchEditor} /> : null}
        <div
          className={editingMatchId ? "sport-summary-panel match-editor-panel is-modal" : "sport-summary-panel match-editor-panel"}
          role={editingMatchId ? "dialog" : undefined}
          aria-modal={editingMatchId ? true : undefined}
          aria-label={editingMatchId ? "Sửa trận đấu" : undefined}
          ref={matchFormRef}
        >
	          <div className="section-title-row">
	            <div>
	              <p className="admin-kicker">Nhập trận</p>
	              <h2>{editingMatchId ? "Sửa trận đấu" : "Tạo trận đấu"}</h2>
	            </div>
	            {editingMatchId ? (
                <button className="icon-only" type="button" onClick={closeMatchEditor} aria-label="Đóng modal sửa trận">
                  <X size={18} />
                </button>
              ) : (
                <Trophy size={26} />
              )}
	          </div>

	          {!editingMatchId ? (
              <button className="ghost-button match-generator-button" type="button" onClick={generateGroupMatches} disabled={generatingGroupMatches}>
	            <Shuffle size={16} />
	            {generatingGroupMatches ? "Đang xếp trận..." : "Random xếp trận/bảng"}
	          </button>
            ) : null}

	          <div className="badminton-form">
            <select
              value={matchDraft.round_type}
              onChange={(event) => {
                const roundType = event.target.value as BadmintonRoundType;
                const groupName: BadmintonGroupName = "A";
                const firstAvailable =
                  getMatchNumberOptions(roundType, getGroupTeamCount(teams, groupName)).find((number) =>
                    !isMatchNumberUsed(matches, roundType, roundType === "group" ? groupName : null, number, editingMatchId)
                  ) ?? 1;
                setMatchDraft({
                  ...matchDraft,
                  round_type: roundType,
                  group_name: roundType === "group" ? groupName : null,
                  match_number: firstAvailable,
                  winning_score: getDefaultWinningScore(roundType, sportSlug),
                  team_one_id: 0,
                  team_two_id: 0,
                  team_one_score: 0,
                  team_two_score: 0,
                  set_scores: [],
                  winner_team_id: null
                });
              }}
            >
              <option value="group">Vòng loại</option>
              <option value="semifinal">Bán kết</option>
              <option value="final">Chung kết</option>
            </select>
            {matchDraft.round_type === "group" ? (
              <>
                <select
                  value={matchDraft.group_name ?? "A"}
                  onChange={(event) => {
                    const groupName = event.target.value as BadmintonGroupName;
                    const firstAvailable =
                      getMatchNumberOptions("group", getGroupTeamCount(teams, groupName)).find((number) =>
                        !isMatchNumberUsed(matches, "group", groupName, number, editingMatchId)
                      ) ??
                      1;
                    setMatchDraft({
                      ...matchDraft,
                      group_name: groupName,
                      match_number: firstAvailable,
                      team_one_id: 0,
                      team_two_id: 0,
                      team_one_score: 0,
                      team_two_score: 0,
                      set_scores: [],
                      winner_team_id: null
                    });
	                  }}
                >
                  <option value="A">Bảng A</option>
                  <option value="B">Bảng B</option>
                </select>
              </>
            ) : null}
            <select
              value={matchDraft.match_number ?? 1}
              onChange={(event) => setMatchDraft({ ...matchDraft, match_number: Number(event.target.value) })}
            >
              {matchNumberOptions.map((number) => {
                const used = isMatchNumberUsed(matches, matchDraft.round_type, matchDraft.group_name, number, editingMatchId);
                return (
                  <option value={number} disabled={used} key={number}>
                    {matchOptionLabel(matchDraft.round_type, matchDraft.group_name, number)}
                    {used ? " (đã có)" : ""}
                  </option>
                );
              })}
            </select>
            {!availableMatchNumbers.length ? (
              <p className="form-hint">
                {roundLabel(matchDraft.round_type, matchDraft.group_name)} đã đủ slot trận. Hãy sửa trận hiện có hoặc chọn vòng khác.
              </p>
            ) : null}
            <label className={matchDraft.is_done ? "done-toggle is-checked" : "done-toggle"}>
              <input
                type="checkbox"
                checked={matchDraft.is_done}
                onChange={(event) => setMatchDraft({ ...matchDraft, is_done: event.target.checked })}
              />
              <span>{matchDraft.is_done ? "Đã xong" : "Chưa xong"}</span>
            </label>
            {isFoosball ? (
              <div className="fixed-score-box">
                <span>Điểm thi</span>
                <strong>10 điểm</strong>
              </div>
            ) : (
              <select
                value={matchDraft.winning_score}
                onChange={(event) => {
                  const winningScore = Number(event.target.value) as WinningScore;
                  setMatchDraft({
                    ...matchDraft,
                    winning_score: winningScore,
                    team_one_score: 0,
                    team_two_score: 0,
                    set_scores: [],
                    winner_team_id: null
                  });
                }}
              >
                {getWinningScoreOptions(matchDraft.round_type, sportSlug).map((score) => (
                  <option value={score} key={score}>Mỗi set {score} điểm</option>
                ))}
              </select>
            )}
            <select
              value={matchDraft.team_one_id}
              onChange={(event) => setMatchDraft({ ...matchDraft, team_one_id: Number(event.target.value) })}
            >
              <option value={0} disabled>Chọn đội thứ nhất</option>
              {matchTeamOptions.map((team) => (
                <option key={team.id} value={team.id}>{displayTeamLabel(team)}</option>
              ))}
            </select>
            <select
              value={matchDraft.team_two_id}
              onChange={(event) => setMatchDraft({ ...matchDraft, team_two_id: Number(event.target.value) })}
            >
              <option value={0} disabled>Chọn đội thứ hai</option>
              {matchTeamOptions.map((team) => (
                <option key={team.id} value={team.id}>{displayTeamLabel(team)}</option>
              ))}
            </select>
            <div className="set-score-grid">
              {getVisibleSetScoreRows(matchDraft).map((setScore, index) => (
                <div className="set-score-row" key={index}>
                  <strong>Set {index + 1}</strong>
                  <input
                    type="number"
                    min={0}
                    max={matchDraft.winning_score}
                    value={setScore.team_one_score}
                    onChange={(event) =>
                      setMatchDraft(updateSetScoreDraft(matchDraft, index, "team_one_score", Number(event.target.value) || 0))
                    }
                    placeholder="Đội 1"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    min={0}
                    max={matchDraft.winning_score}
                    value={setScore.team_two_score}
                    onChange={(event) =>
                      setMatchDraft(updateSetScoreDraft(matchDraft, index, "team_two_score", Number(event.target.value) || 0))
                    }
                    placeholder="Đội 2"
                  />
                </div>
              ))}
            </div>
            <div className={draftWinnerName ? "auto-winner-box is-ready" : "auto-winner-box"}>
              <span>Đội thắng tự tính</span>
              <strong>
                {draftWinnerName
                  ? `${draftWinnerName} thắng ${formatSetScoreResult(matchDraft)}`
                  : getWinnerHint(matchDraft)}
              </strong>
            </div>
            <textarea
              value={matchDraft.notes}
              onChange={(event) => setMatchDraft({ ...matchDraft, notes: event.target.value })}
              placeholder="Ghi chú"
            />
            <button className="primary-action" type="button" onClick={saveMatch}>
              {editingMatchId ? <Save size={18} /> : <Plus size={18} />}
              {editingMatchId ? "Lưu trận" : "Tạo trận"}
            </button>
            {editingMatchId ? (
              <button
                className="ghost-button"
                type="button"
                onClick={closeMatchEditor}
              >
                Hủy sửa
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section className="sport-summary-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">Sơ đồ trực quan</p>
            <h2>Đội nào thi đấu với đội nào</h2>
          </div>
          <BarChart3 size={26} />
        </div>
        {loadingTournament ? <SkeletonRows count={4} /> : <BadmintonBracket matches={matches} teams={teams} />}
      </section>

      <section className="sport-summary-panel">
        <div className="section-title-row">
	          <div>
	            <p className="admin-kicker">Bảng điểm vòng loại</p>
	            <h2>Điểm thắng để vào bán kết chéo</h2>
	          </div>
          <Trophy size={26} />
        </div>
        {loadingTournament ? <SkeletonCardGrid count={2} /> : <BadmintonStandings matches={matches} />}
      </section>

      <section className="sport-summary-panel">
        <div className="section-title-row">
          <div>
            <p className="admin-kicker">Danh sách trận</p>
            <h2>{matchSearch.trim() ? `${filteredMatches.length}/${matches.length} trận đấu` : `${matches.length} trận đấu`}</h2>
          </div>
        </div>
        <div className="match-search-toolbar">
          <label className="search-box match-search-box">
            <Search size={18} />
            <input
              value={matchSearch}
              onChange={(event) => setMatchSearch(event.target.value)}
              placeholder="Tìm tên đội, vòng loại, bán kết, trận 3..."
            />
          </label>
        </div>
        <div className="match-list">
          {loadingTournament ? <SkeletonMatchList count={4} /> : filteredMatches.map((match) => (
            <article className="match-row" key={match.id}>
              <div>
                <strong>{matchLabel(match)}</strong>
                <div className="match-scoreline">
                  <div className="match-team-line">
                    <span className={match.winner_team_id === match.team_one_id ? "match-team-name is-winner" : "match-team-name"}>
                      {displayMatchTeamLabel(match, "one")}
                    </span>
                    <span className={match.winner_team_id === match.team_one_id ? "match-score-pill is-winner-score" : "match-score-pill"}>
                      {displayMatchTeamScore(match, "one")}
                    </span>
                  </div>
                  <div className="match-team-line">
                    <span className={match.winner_team_id === match.team_two_id ? "match-team-name is-winner" : "match-team-name"}>
                      {displayMatchTeamLabel(match, "two")}
                    </span>
                    <span className={match.winner_team_id === match.team_two_id ? "match-score-pill is-winner-score" : "match-score-pill"}>
                      {displayMatchTeamScore(match, "two")}
                    </span>
                  </div>
                </div>
	                <small className={match.winner_team_name ? "winner-note" : ""}>
	                  {formatMatchScoreDetail(match)}
	                  {match.winner_team_name ? ` | Đội thắng: ${displayWinnerTeamLabel(match)}` : ` | ${getWinnerHint(match)}`}
                    {match.is_done ? " | Done" : ""}
	                </small>
              </div>
              <div className="row-actions">
	                <button
	                  className="icon-only"
	                  type="button"
	                  onClick={() => editMatch(match)}
	                  aria-label="Sửa trận"
	                >
	                  <Pencil size={16} />
	                </button>
	                {confirmingDeleteMatchId === match.id ? (
	                  <div className="inline-confirm-actions" aria-label={`Xác nhận xóa ${matchLabel(match)}`}>
	                    <button className="confirm-delete-button" type="button" onClick={() => removeMatch(match)}>
	                      Xác nhận
	                    </button>
	                    <button className="cancel-delete-button" type="button" onClick={() => setConfirmingDeleteMatchId(null)}>
	                      Hủy
	                    </button>
	                  </div>
	                ) : (
	                  <button
	                    className="icon-only danger"
	                    type="button"
	                    onClick={() => setConfirmingDeleteMatchId(match.id)}
	                    aria-label="Xóa trận"
	                  >
	                    <Trash2 size={16} />
	                  </button>
	                )}
	              </div>
            </article>
          ))}
          {!loadingTournament && !filteredMatches.length ? <p className="empty-search">Không tìm thấy trận phù hợp.</p> : null}
        </div>
      </section>
      <Footer />
    </main>
  );
}

function MatchManagementGuideModal({
  sportName,
  isFoosball,
  onClose
}: {
  sportName: string;
  isFoosball: boolean;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="bracket-modal match-guide-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Hướng dẫn quản lý trận đấu ${sportName}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <p className="admin-kicker">Hướng dẫn</p>
            <h2>Quản lý trận đấu {sportName}</h2>
          </div>
          <button className="icon-only" type="button" onClick={onClose} aria-label="Đóng hướng dẫn">
            <X size={18} />
          </button>
        </div>

        <div className="guide-step-list">
          <article>
            <strong>1. Đọc bảng & đội</strong>
            <p>Bấm “Đọc bảng & đội” để lấy danh sách đội từ sheet của môn hiện tại, sau đó kiểm tra đội ở bảng A/B.</p>
          </article>
          <article>
            <strong>2. Tạo trận vòng loại</strong>
            <p>Bấm “Random xếp trận/bảng” để tạo các cặp vòng loại còn thiếu, hoặc chọn vòng/bảng/trận rồi tạo thủ công.</p>
          </article>
          <article>
            <strong>3. Nhập kết quả</strong>
            <p>
              Chọn 2 đội, nhập điểm từng set. {isFoosball ? "Bi lắc cố định 10 điểm mỗi set." : "Vòng loại 15 điểm, chung kết 21 điểm; bán kết có thể chọn 15 hoặc 21 điểm."}
            </p>
          </article>
          <article>
            <strong>4. Lưu trạng thái</strong>
            <p>Khi trận đã hoàn tất, bật “Đã xong”, kiểm tra đội thắng tự tính rồi bấm “Lưu trận”.</p>
          </article>
          <article>
            <strong>5. Sửa hoặc xóa trận</strong>
            <p>Dùng biểu tượng bút ở danh sách trận để sửa trong modal. Dùng biểu tượng thùng rác và xác nhận nếu cần xóa trận.</p>
          </article>
        </div>
      </section>
    </div>
  );
}

function BadmintonBracket({ matches, teams }: { matches: BadmintonMatch[]; teams: BadmintonTeam[] }) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const sections = [
    {
      title: "Bảng A",
      slots: getRoundRobinMatchCount(getGroupTeamCount(teams, "A")),
      matches: matches.filter((match) => match.round_type === "group" && match.group_name === "A")
    },
    {
      title: "Bảng B",
      slots: getRoundRobinMatchCount(getGroupTeamCount(teams, "B")),
      matches: matches.filter((match) => match.round_type === "group" && match.group_name === "B")
    },
    { title: "Bán kết", slots: 2, matches: matches.filter((match) => match.round_type === "semifinal") },
    { title: "Chung kết", slots: 1, matches: matches.filter((match) => match.round_type === "final") }
  ];
  const selectedSection = sections.find((section) => section.title === activeSection) ?? null;

  return (
    <>
      <div className="bracket-grid">
        {sections.map((section) => {
          const winners = unique(section.matches.map(displayWinnerTeamLabel).filter(Boolean));

          return (
            <button className="bracket-column" type="button" key={section.title} onClick={() => setActiveSection(section.title)}>
              <div className="bracket-column-head">
                <h3>{section.title}</h3>
                <span>{section.matches.length}/{section.slots} trận</span>
              </div>
              {section.matches.length ? (
                <div className="bracket-preview">
                  {section.matches.slice(0, 2).map((match) => (
                    <div className="bracket-preview-row" key={match.id}>
	                      <span>{shortMatchLabel(match)}</span>
	                      <strong>
	                        {formatSetScoreResult(match)}
	                      </strong>
                    </div>
                  ))}
                  {section.matches.length > 2 ? <small>+{section.matches.length - 2} trận khác</small> : null}
                </div>
              ) : (
                <p className="empty-bracket">Chưa có trận</p>
              )}
              {winners.length ? (
                <div className="bracket-winner-summary">
                  <small>Đội thắng</small>
                  <strong>{winners.join(", ")}</strong>
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      {selectedSection ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setActiveSection(null)}>
          <section
            className="bracket-modal"
            role="dialog"
            aria-modal="true"
            aria-label={selectedSection.title}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-head">
              <div>
                <p className="admin-kicker">Chi tiết sơ đồ</p>
                <h2>{selectedSection.title}</h2>
              </div>
              <button className="icon-only" type="button" onClick={() => setActiveSection(null)} aria-label="Đóng modal">
                <X size={18} />
              </button>
            </div>

            <div className="modal-match-list">
              {Array.from({ length: selectedSection.slots }, (_, index) => index + 1).map((slot) => {
                const match = selectedSection.matches.find((item) => (item.match_number ?? 1) === slot);
                return match ? (
                  <BracketMatchCard match={match} key={slot} />
                ) : (
                  <article className="bracket-empty-slot" key={slot}>
                    <strong>{selectedSection.title === "Chung kết" ? "Chung kết" : `${selectedSection.title} ${slot}`}</strong>
                    <span>Chưa tạo trận</span>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

function BracketMatchCard({ match }: { match: BadmintonMatch }) {
  return (
    <article className="bracket-match">
      <small>{matchLabel(match)}</small>
	      <div className={match.winner_team_id === match.team_one_id ? "winner-line" : ""}>
	        <span>{displayMatchTeamLabel(match, "one")}</span>
	        <strong>{displayMatchTeamScore(match, "one")}</strong>
	      </div>
	      <div className={match.winner_team_id === match.team_two_id ? "winner-line" : ""}>
	        <span>{displayMatchTeamLabel(match, "two")}</span>
	        <strong>{displayMatchTeamScore(match, "two")}</strong>
	      </div>
	      <small className="set-score-detail">{formatMatchScoreDetail(match)}</small>
      {match.winner_team_name ? (
        <p className="modal-winner">
	          Đội thắng: <strong>{displayWinnerTeamLabel(match)}</strong>
        </p>
      ) : null}
    </article>
  );
}

function BadmintonStandings({ matches }: { matches: BadmintonMatch[] }) {
  const groups: BadmintonGroupName[] = ["A", "B"];

  return (
    <div className="standings-grid">
      {groups.map((group) => {
        const rows = calculateGroupStandings(matches, group);

        return (
          <section className="standings-card" key={group}>
            <div className="standings-head">
              <h3>Bảng {group}</h3>
              <span>Thắng 1, thua 0</span>
            </div>
            {rows.length ? (
              <div className="standings-list">
                {rows.map((row, index) => (
	                  <article className={index < 2 && row.points > 0 ? "standing-row is-qualified" : "standing-row"} key={row.teamId}>
	                    <div>
	                      <strong>{row.teamName}</strong>
	                      <small>{index < 2 && row.points > 0 ? "Tạm vào bán kết" : `${row.played} trận đã tính`}</small>
	                    </div>
                    <div className="standing-numbers">
                      <span>{row.points} điểm</span>
                      <small>HS {row.diff > 0 ? `+${row.diff}` : row.diff}</small>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="empty-bracket">Chưa có trận vòng loại bảng {group}.</p>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Footer() {
  return <footer className="site-footer">Raymond © 2026</footer>;
}

function SkeletonLine({ width = "100%", className = "" }: { width?: string; className?: string }) {
  return <span className={className ? `skeleton-line ${className}` : "skeleton-line"} style={{ width }} />;
}

function SkeletonMetricGrid({ count, className }: { count: number; className: string }) {
  return (
    <section className={className} aria-label="Đang tải dữ liệu">
      {Array.from({ length: count }, (_, index) => (
        <div className="skeleton-metric-card" key={index}>
          <SkeletonLine width="34px" className="skeleton-dot" />
          <SkeletonLine width="54px" className="skeleton-number" />
          <SkeletonLine width="72px" />
        </div>
      ))}
    </section>
  );
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <article className="bu-count-row skeleton-row" key={index}>
          <SkeletonLine width={index % 2 ? "46%" : "58%"} />
          <div className="bu-count-value">
            <SkeletonLine width="34px" />
            <SkeletonLine width="140px" />
          </div>
        </article>
      ))}
    </>
  );
}

function SkeletonCardGrid({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <article className="skeleton-card-block" key={index}>
          <SkeletonLine width="42px" className="skeleton-dot" />
          <div>
            <SkeletonLine width={index % 2 ? "62%" : "74%"} />
            <SkeletonLine width="44%" />
          </div>
          <SkeletonLine width="58px" />
        </article>
      ))}
    </>
  );
}

function SkeletonTableRows({ rows, columns }: { rows: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr className="skeleton-table-row" key={rowIndex}>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <td key={columnIndex}>
              <SkeletonLine width={columnIndex === 0 ? "76%" : "58%"} />
              {columnIndex === 0 ? <SkeletonLine width="46%" /> : null}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function SkeletonTeamRoster() {
  return (
    <div className="team-roster-grid">
      {["A", "B"].map((group) => (
        <section className="team-group-column skeleton-group-column" key={group}>
          <div className="team-group-head">
            <SkeletonLine width="82px" />
            <SkeletonLine width="48px" />
          </div>
          <div className="team-list">
            {Array.from({ length: 4 }, (_, index) => (
              <article className="team-card skeleton-row" key={index}>
                <SkeletonLine width={index % 2 ? "68%" : "82%"} />
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function SkeletonMatchList({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <article className="match-row skeleton-match-row" key={index}>
          <div>
            <SkeletonLine width={index % 2 ? "42%" : "54%"} />
            <div className="match-scoreline">
              <SkeletonLine width="88%" />
              <SkeletonLine width="80%" />
            </div>
            <SkeletonLine width="66%" />
          </div>
          <div className="row-actions">
            <SkeletonLine width="34px" className="skeleton-action" />
            <SkeletonLine width="34px" className="skeleton-action" />
          </div>
        </article>
      ))}
    </>
  );
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="metric">
      {icon}
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function TeamGroupList({
  groupName,
  teams,
  onEditTeam,
  onRemoveTeam
}: {
  groupName: BadmintonGroupName | null;
  teams: BadmintonTeam[];
  onEditTeam: (team: BadmintonTeam) => void;
  onRemoveTeam: (team: BadmintonTeam) => void;
}) {
  const groupClass = groupName ? `group-${groupName.toLowerCase()}` : "group-none";

  return (
    <section className={`team-group-column ${groupClass}`}>
      <div className="team-group-head">
        <h3>{groupName ? `Bảng ${groupName}` : "Chưa xếp bảng"}</h3>
        <span>{teams.length} đội</span>
      </div>
      <div className="team-list">
        {teams.length ? (
          teams.map((team) => (
            <article className={team.group_name ? `team-card group-${team.group_name.toLowerCase()}` : "team-card"} key={team.id}>
              <strong>{displayTeamLabel(team)}</strong>
              <div className="row-actions">
                <button className="icon-only" type="button" onClick={() => onEditTeam(team)} aria-label="Sửa đội">
                  <Pencil size={16} />
                </button>
                <button className="icon-only danger" type="button" onClick={() => onRemoveTeam(team)} aria-label="Xóa đội">
                  <Trash2 size={16} />
                </button>
              </div>
            </article>
          ))
        ) : (
          <p className="empty-bracket">Chưa có đội.</p>
        )}
      </div>
    </section>
  );
}

function unique(values: string[]) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, "vi"));
}

function countBy(values: string[]) {
  const counts = new Map<string, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  return Array.from(counts, ([name, count]) => ({ name, count })).sort(
    (left, right) => right.count - left.count || left.name.localeCompare(right.name, "vi")
  );
}

function formatDate(value: string, language: LandingLanguage = "vi") {
  if (!value) return "13/06/2026";
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat(language === "en" ? "en-US" : "vi-VN", {
    day: "2-digit",
    month: language === "en" ? "short" : "2-digit",
    year: "numeric"
  }).format(date);
}

function formatTime(value: string) {
  if (!value) return "";
  const [hour, minute] = value.split(":");
  return `${hour}:${minute}`;
}

function genderLabel(value: Participant["gender"]) {
  if (value === "male") return "Nam";
  if (value === "female") return "Nữ";
  return "";
}

function isTeamManagedSportSlug(value: string) {
  return (
    value === "badminton-men-doubles" ||
    value === "badminton-women-doubles" ||
    value === "pickleball-doubles" ||
    value === "foosball-doubles"
  );
}

function isBadmintonScheduleSportSlug(value: string) {
  return value === "badminton-men-doubles" || value === "badminton-women-doubles";
}

function getTeamSheetTabName(sportSlug: string) {
  if (sportSlug === "pickleball-doubles") return "pickleballs";
  if (sportSlug === "foosball-doubles") return "bilac";
  return "badminton";
}

function participantSportLabel(value: string) {
  return value.replace(/\s+đôi\b/giu, "");
}

function getDisplayedSportCount(sport: Pick<Sport, "id" | "slug"> & { count?: number }, sportCounts?: Map<number, number>) {
  if (sport.slug === "football") return sportCounts?.get(sport.id) ?? sport.count ?? fallbackFootballPlayerCount;
  return sportCounts?.get(sport.id) ?? sport.count ?? 0;
}

function getSportCountUnit(sportSlug: string) {
  if (isTeamManagedSportSlug(sportSlug)) return "đội";
  if (sportSlug === "football") return "cầu thủ";
  return "đăng ký";
}

function getLandingSportName(sport: Pick<Sport, "slug" | "name">, language: LandingLanguage) {
  return landingSportLabels[sport.slug]?.[language].name ?? sport.name;
}

function getLandingSportCategory(sport: Pick<Sport, "slug" | "category">, language: LandingLanguage) {
  return landingSportLabels[sport.slug]?.[language].category ?? sport.category;
}

function getLandingSportCountUnit(sportSlug: string, language: LandingLanguage) {
  if (!isTeamManagedSportSlug(sportSlug) && sportSlug !== "football") {
    return language === "en" ? "registrations" : "đăng ký";
  }
  const copy = landingCopy[language];
  return sportSlug === "football" ? copy.playerUnit : copy.teamUnit;
}

function emptyTeamDraft(): BadmintonTeamDraft {
  return {
    name: "",
    player_one_name: "",
    player_two_name: "",
    group_name: null,
    source_registration_id: null,
    source_type: "sheet"
  };
}

function emptyMatchDraft(sportSlug = ""): BadmintonMatchDraft {
  return {
    round_type: "group",
    group_name: "A",
    match_number: 1,
    winning_score: getDefaultWinningScore("group", sportSlug),
    team_one_id: 0,
    team_two_id: 0,
    team_one_score: 0,
    team_two_score: 0,
    set_scores: [],
    winner_team_id: null,
    scheduled_court: null,
    scheduled_start: "",
    scheduled_end: "",
    is_done: false,
    notes: ""
  };
}

function toMatchDraft(match: BadmintonMatch): BadmintonMatchDraft {
  return {
    round_type: match.round_type,
    group_name: match.group_name,
    match_number: match.match_number,
    winning_score: match.winning_score,
    team_one_id: match.team_one_id,
    team_two_id: match.team_two_id,
    team_one_score: match.team_one_score,
    team_two_score: match.team_two_score,
    set_scores: match.set_scores,
    winner_team_id: match.winner_team_id,
    scheduled_court: match.scheduled_court,
    scheduled_start: match.scheduled_start,
    scheduled_end: match.scheduled_end,
    is_done: match.is_done,
    notes: match.notes
  };
}

function normalizeMatchDraftForSport(match: BadmintonMatchDraft, sportSlug: string): BadmintonMatchDraft {
  return {
    ...match,
    winning_score: sportSlug === "foosball-doubles" ? 10 : match.winning_score,
    scheduled_court: null,
    scheduled_start: "",
    scheduled_end: ""
  };
}

function compareMatchOrder(left: Pick<BadmintonMatch, "round_type" | "group_name" | "match_number" | "id">, right: Pick<BadmintonMatch, "round_type" | "group_name" | "match_number" | "id">) {
  return (
    getRoundOrder(left.round_type) - getRoundOrder(right.round_type) ||
    (left.group_name ?? "").localeCompare(right.group_name ?? "") ||
    (left.match_number ?? 0) - (right.match_number ?? 0) ||
    left.id - right.id
  );
}

function getRoundOrder(roundType: BadmintonRoundType) {
  if (roundType === "group") return 1;
  if (roundType === "quarterfinal") return 2;
  if (roundType === "semifinal") return 3;
  return 4;
}

function formatScheduleTime(match: Pick<BadmintonMatch, "scheduled_start" | "scheduled_end">) {
  return [match.scheduled_start, match.scheduled_end].filter(Boolean).join("-");
}

function formatScheduleBadge(match: Pick<BadmintonMatch, "scheduled_court" | "scheduled_start" | "scheduled_end">) {
  const time = formatScheduleTime(match);
  if (!match.scheduled_court && !time) return "Chưa xếp lịch";
  if (!match.scheduled_court) return time;
  return [`Sân ${match.scheduled_court ?? "-"}`, time].filter(Boolean).join(" · ");
}

function getDraftWinnerName(match: BadmintonMatchDraft, teams: BadmintonTeam[]) {
  const winnerId = getDraftWinnerId(match);
  return teams.find((team) => team.id === winnerId)?.name ?? "";
}

function getMatchTeamOptions(teams: BadmintonTeam[], match: BadmintonMatchDraft) {
  if (match.round_type !== "group" || !match.group_name) return teams;
  return teams.filter((team) => team.group_name === match.group_name);
}

function displayTeamName(team: Pick<BadmintonTeam, "name" | "group_name">) {
  if (!team.group_name) return team.name;
  return team.name.replace(new RegExp(`^Bảng ${team.group_name}\\s*-\\s*`, "i"), "");
}

function displayTeamLabel(team: Pick<BadmintonTeam, "name" | "group_name" | "player_one_name" | "player_two_name">) {
  const shortNames = [lastNameWords(team.player_one_name, 2), lastNameWords(team.player_two_name, 2)].filter(Boolean).join(" / ");
  return [displayTeamName(team), shortNames].filter(Boolean).join(" - ");
}

function displayMatchTeamLabel(match: BadmintonMatch, side: "one" | "two") {
  if (side === "one") {
    return displayTeamLabel({
      name: match.team_one_name,
      group_name: match.group_name,
      player_one_name: match.team_one_player_one_name,
      player_two_name: match.team_one_player_two_name
    });
  }

  return displayTeamLabel({
    name: match.team_two_name,
    group_name: match.group_name,
    player_one_name: match.team_two_player_one_name,
    player_two_name: match.team_two_player_two_name
  });
}

function displayWinnerTeamLabel(match: BadmintonMatch) {
  if (!match.winner_team_name) return "";
  return displayTeamLabel({
    name: match.winner_team_name,
    group_name: match.group_name,
    player_one_name: match.winner_player_one_name ?? "",
    player_two_name: match.winner_player_two_name ?? ""
  });
}

function lastNameWords(value: string, count: number) {
  const words = value.trim().split(/\s+/).filter(Boolean);
  return words.slice(Math.max(0, words.length - count)).join(" ");
}

function getDraftWinnerId(
  match: Pick<BadmintonMatchDraft, "round_type" | "team_one_id" | "team_two_id" | "set_scores" | "winning_score">
) {
  const setWins = countSetWins(match.set_scores, match.winning_score);
  const winsNeeded = getWinsNeeded(match.round_type);
  if (setWins.teamOne >= winsNeeded) {
    return match.team_one_id;
  }
  if (setWins.teamTwo >= winsNeeded) {
    return match.team_two_id;
  }
  return null;
}

function getWinningScoreOptions(roundType: BadmintonRoundType, sportSlug = ""): WinningScore[] {
  if (sportSlug === "foosball-doubles") return [10];
  if (roundType === "group") return [15];
  if (roundType === "final") return [21];
  return [15, 21];
}

function getDefaultWinningScore(roundType: BadmintonRoundType, sportSlug = ""): WinningScore {
  if (sportSlug === "foosball-doubles") return 10;
  if (roundType === "final") return 21;
  return 15;
}

function getSetScoreRows(setScores: BadmintonSetScore[]) {
  return Array.from({ length: 3 }, (_, index) => setScores[index] ?? { team_one_score: 0, team_two_score: 0 });
}

function getVisibleSetScoreRows(match: Pick<BadmintonMatchDraft, "round_type" | "set_scores">) {
  return getSetScoreRows(match.set_scores).slice(0, match.round_type === "group" ? 1 : 3);
}

function updateSetScoreDraft(
  match: BadmintonMatchDraft,
  index: number,
  side: keyof BadmintonSetScore,
  value: number
): BadmintonMatchDraft {
  const setScores = getVisibleSetScoreRows(match).map((setScore) => ({ ...setScore }));
  setScores[index][side] = Math.min(match.winning_score, Math.max(0, Math.trunc(value)));
  const trimmedSetScores = trimEmptyTrailingSetScores(setScores);
  const setWins = countSetWins(trimmedSetScores, match.winning_score);

  return {
    ...match,
    set_scores: trimmedSetScores,
    team_one_score: setWins.teamOne,
    team_two_score: setWins.teamTwo,
    winner_team_id: null
  };
}

function trimEmptyTrailingSetScores(setScores: BadmintonSetScore[]) {
  let lastFilledIndex = -1;
  setScores.forEach((setScore, index) => {
    if (setScore.team_one_score > 0 || setScore.team_two_score > 0) lastFilledIndex = index;
  });
  return lastFilledIndex >= 0 ? setScores.slice(0, lastFilledIndex + 1) : [];
}

function countSetWins(setScores: BadmintonSetScore[], winningScore: WinningScore) {
  return setScores.reduce(
    (wins, setScore) => {
      if (setScore.team_one_score === winningScore && setScore.team_one_score > setScore.team_two_score) {
        wins.teamOne += 1;
      } else if (setScore.team_two_score === winningScore && setScore.team_two_score > setScore.team_one_score) {
        wins.teamTwo += 1;
      }
      return wins;
    },
    { teamOne: 0, teamTwo: 0 }
  );
}

function getWinsNeeded(roundType: BadmintonRoundType) {
  return roundType === "group" ? 1 : 2;
}

function hasSetScoreDetails(match: Pick<BadmintonMatch, "set_scores"> | Pick<BadmintonMatchDraft, "set_scores">) {
  return match.set_scores.length > 0;
}

function hasMatchScore(match: BadmintonMatch) {
  return hasSetScoreDetails(match) || match.team_one_score > 0 || match.team_two_score > 0;
}

function displayMatchTeamScore(
  match: Pick<BadmintonMatch, "round_type" | "team_one_score" | "team_two_score" | "set_scores" | "winning_score">,
  side: "one" | "two"
) {
  if (!hasSetScoreDetails(match)) {
    return side === "one" ? match.team_one_score : match.team_two_score;
  }
  if (match.round_type === "group") {
    const game = match.set_scores[0];
    return side === "one" ? game?.team_one_score ?? 0 : game?.team_two_score ?? 0;
  }
  return side === "one" ? countSetWins(match.set_scores, match.winning_score).teamOne : countSetWins(match.set_scores, match.winning_score).teamTwo;
}

function formatSetScoreResult(
  match: Pick<BadmintonMatch, "round_type" | "team_one_score" | "team_two_score" | "set_scores" | "winning_score"> | BadmintonMatchDraft
) {
  if (!hasSetScoreDetails(match)) return `${match.team_one_score}-${match.team_two_score}`;
  if (match.round_type === "group") {
    const game = match.set_scores[0];
    return game ? `${game.team_one_score}-${game.team_two_score}` : "0-0";
  }
  const setWins = countSetWins(match.set_scores, match.winning_score);
  return `${setWins.teamOne}-${setWins.teamTwo}`;
}

function formatMatchScoreDetail(
  match: Pick<BadmintonMatch, "round_type" | "team_one_score" | "team_two_score" | "set_scores" | "winning_score"> | BadmintonMatchDraft
) {
  if (!hasSetScoreDetails(match)) return `Tỷ số: ${match.team_one_score}-${match.team_two_score}`;
  if (match.round_type === "group") return `Tỷ số: ${formatSetScoreResult(match)}`;
  return `${formatSetScoreResult(match)} (${match.set_scores.map((setScore) => `${setScore.team_one_score}-${setScore.team_two_score}`).join(", ")})`;
}

function getWinnerHint(match: Pick<BadmintonMatch, "round_type" | "winning_score"> | Pick<BadmintonMatchDraft, "round_type" | "winning_score">) {
  return match.round_type === "group"
    ? `Nhập đội thắng 1 game ${match.winning_score} điểm`
    : `Nhập đội thắng 2 set (${match.winning_score} điểm/set)`;
}

function isMatchNumberUsed(
  matches: BadmintonMatch[],
  roundType: BadmintonRoundType,
  groupName: BadmintonGroupName | null,
  matchNumber: number,
  editingMatchId: number | null
) {
  return matches.some(
    (match) =>
      match.id !== editingMatchId &&
      match.round_type === roundType &&
      (roundType !== "group" || match.group_name === groupName) &&
      match.match_number === matchNumber
  );
}

function getRoundRobinPairs(teams: BadmintonTeam[]) {
  const pairs: Array<[BadmintonTeam, BadmintonTeam]> = [];
  for (let leftIndex = 0; leftIndex < teams.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < teams.length; rightIndex += 1) {
      pairs.push([teams[leftIndex], teams[rightIndex]]);
    }
  }
  return pairs;
}

function teamPairKey(teamOneId: number, teamTwoId: number) {
  return [teamOneId, teamTwoId].sort((left, right) => left - right).join("-");
}

function shuffleItems<T>(items: T[]) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

function getGroupTeamCount(teams: BadmintonTeam[], groupName: BadmintonGroupName | null) {
  if (!groupName) return 4;
  return teams.filter((team) => team.group_name === groupName).length || 4;
}

function getRoundRobinMatchCount(teamCount: number) {
  return Math.max(1, Math.min(10, (teamCount * (teamCount - 1)) / 2));
}

function getMatchNumberOptions(roundType: BadmintonRoundType, groupTeamCount = 4) {
  const total =
    roundType === "group"
      ? getRoundRobinMatchCount(groupTeamCount)
      : roundType === "quarterfinal"
        ? 4
        : roundType === "semifinal"
          ? 2
          : 1;
  return Array.from({ length: total }, (_, index) => index + 1);
}

function matchOptionLabel(roundType: BadmintonRoundType, groupName: BadmintonGroupName | null, matchNumber: number) {
  if (roundType === "group") return `Bảng ${groupName ?? "A"} - Trận ${matchNumber}`;
  if (roundType === "quarterfinal") return `Tứ kết ${matchNumber}`;
  if (roundType === "semifinal") return `Bán kết ${matchNumber}`;
  return "Chung kết";
}

function roundLabel(roundType: BadmintonRoundType, groupName: BadmintonGroupName | null) {
  if (roundType === "group") return `Bảng ${groupName ?? "A"}`;
  if (roundType === "quarterfinal") return "Tứ kết";
  if (roundType === "semifinal") return "Bán kết";
  return "Chung kết";
}

function calculateGroupStandings(matches: BadmintonMatch[], group: BadmintonGroupName) {
  const rows = new Map<
    number,
    {
      teamId: number;
      teamName: string;
      played: number;
      points: number;
      scoreFor: number;
      scoreAgainst: number;
      diff: number;
    }
  >();
  const groupMatches = matches.filter((match) => match.round_type === "group" && match.group_name === group);

  for (const match of groupMatches) {
    const hasWinner = Boolean(match.winner_team_id);
    const teamOnePointsFor = hasSetScoreDetails(match)
      ? match.set_scores.reduce((total, setScore) => total + setScore.team_one_score, 0)
      : match.team_one_score;
    const teamTwoPointsFor = hasSetScoreDetails(match)
      ? match.set_scores.reduce((total, setScore) => total + setScore.team_two_score, 0)
      : match.team_two_score;
    const teams = [
      {
        id: match.team_one_id,
        name: displayMatchTeamLabel(match, "one"),
        scoreFor: teamOnePointsFor,
        scoreAgainst: teamTwoPointsFor
      },
      {
        id: match.team_two_id,
        name: displayMatchTeamLabel(match, "two"),
        scoreFor: teamTwoPointsFor,
        scoreAgainst: teamOnePointsFor
      }
    ];

    for (const team of teams) {
      const row =
        rows.get(team.id) ??
        {
          teamId: team.id,
          teamName: team.name,
          played: 0,
          points: 0,
          scoreFor: 0,
          scoreAgainst: 0,
          diff: 0
      };
      row.played += hasWinner ? 1 : 0;
      row.points += match.winner_team_id === team.id ? 1 : 0;
      row.scoreFor += hasWinner ? team.scoreFor : 0;
      row.scoreAgainst += hasWinner ? team.scoreAgainst : 0;
      row.diff = row.scoreFor - row.scoreAgainst;
      rows.set(team.id, row);
    }
  }

  return Array.from(rows.values()).sort(
    (left, right) =>
      right.points - left.points ||
      right.diff - left.diff ||
      right.scoreFor - left.scoreFor ||
      left.teamName.localeCompare(right.teamName, "vi")
  );
}

function matchLabel(match: Pick<BadmintonMatch, "round_type" | "group_name" | "match_number">) {
  if (match.round_type === "group") {
    return `Vòng loại bảng ${match.group_name ?? "A"} - Trận ${match.match_number ?? 1}`;
  }
  if (match.round_type === "quarterfinal") return `Tứ kết ${match.match_number ?? 1}`;
  if (match.round_type === "semifinal") return `Bán kết ${match.match_number ?? 1}`;
  return "Chung kết";
}

function filterMatchesByQuery(matches: BadmintonMatch[], query: string) {
  const terms = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return matches;

  return matches.filter((match) => {
    const haystack = buildMatchSearchText(match);
    return terms.every((term) => haystack.includes(term));
  });
}

function buildMatchSearchText(match: BadmintonMatch) {
  const matchNumber = match.match_number ?? 1;
  return normalizeSearchText(
    [
      matchLabel(match),
      shortMatchLabel(match),
      roundLabel(match.round_type, match.group_name),
      match.round_type,
      match.group_name ? `Bảng ${match.group_name}` : "",
      match.group_name ? `bang ${match.group_name}` : "",
      `Trận ${matchNumber}`,
      `tran ${matchNumber}`,
      `Match ${matchNumber}`,
      displayMatchTeamLabel(match, "one"),
      displayMatchTeamLabel(match, "two"),
      displayWinnerTeamLabel(match),
      formatSetScoreResult(match),
      formatMatchScoreDetail(match),
      match.scheduled_court ? `Sân ${match.scheduled_court}` : "",
      match.scheduled_court ? `san ${match.scheduled_court}` : "",
      match.scheduled_start,
      match.scheduled_end,
      formatScheduleTime(match),
      formatScheduleBadge(match),
      match.is_done ? "done đã xong da xong hoàn thành hoan thanh" : "chưa xong chua xong chưa hoàn thành chua hoan thanh",
      match.notes
    ].join(" ")
  );
}

function normalizeSearchText(value: string) {
  return value
    .toLocaleLowerCase("vi-VN")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .replace(/[^\p{L}\p{N}\s-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function shortMatchLabel(match: Pick<BadmintonMatch, "round_type" | "match_number">) {
  if (match.round_type === "group") return `Trận ${match.match_number ?? 1}`;
  return matchLabel({ ...match, group_name: null });
}

export default App;
